import { Component, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Search,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  SkipBack,
  SkipForward,
  Play,
  Pause,
  Settings2,
  Columns3,
  FileDown,
  FileSpreadsheet,
  Printer,
  Locate,
  Loader2,
} from "lucide-react";
import { subDays, subHours, startOfDay, endOfDay, format } from "date-fns";
import { loadXlsx } from "@/lib/xlsx-lazy";
import { cn } from "@/lib/utils";
import type { Vehicle, DetailedPosition, VehicleEvent, RouteStop, Trip } from "@/types";
import {
  detectPowerCuts,
  powerAlarmLabel,
  vehicleVoltageReadings,
  type PowerCut,
  type VoltageReading,
} from "@/lib/power-cut-detection";
import { computeTileViewport, downsamplePoints, OSM_TILE_SIZE } from "@/lib/reports-export";
import { filterValidPositions, isValidCoordinate, haversineKm } from "@/lib/position-validation";
import { filterDeletedPositions } from "@/lib/history-deletion-filter";
import {
  useVehicleRoutePositions,
  useVehicleTrips,
  useVehicleStops,
  useVehicleSummary,
  useVehicleEvents,
  useGeofences,
  useHistoryDeletions,
} from "@/hooks/queries";
import { reportsService } from "@/services/reports.service";
import { useBatchReverseGeocode } from "@/hooks/use-reverse-geocode";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import logoAsset from "@/assets/gps-drive-logo-v3.png.asset.json";
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  InfoWindow,
  Polyline as GooglePolyline,
  useMap as useGoogleMap,
  useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps";
import {
  GOOGLE_MAPS_CLEAN_STYLE,
  resolveGoogleMapId,
  hasCustomGoogleMapId,
} from "@/lib/google-maps-style";
import { useGoogleMapsAuthFailure } from "@/hooks/use-google-maps-auth-failure";
import { LicensePlate } from "@/components/license-plate";

// ============================================================================
// Período
// ============================================================================

type Period = "hoje" | "ontem" | "6h" | "12h" | "24h" | "3d" | "7d" | "custom";

const PERIOD_OPTIONS: { key: Period; label: string }[] = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "6h", label: "Últimas 6 horas" },
  { key: "12h", label: "Últimas 12 horas" },
  { key: "24h", label: "Últimas 24 horas" },
  { key: "3d", label: "Últimos 3 dias" },
  { key: "7d", label: "Últimos 7 dias" },
  { key: "custom", label: "Personalizado" },
];

interface CustomRange {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
}

function periodRange(period: Period, custom: CustomRange): { from: Date; to: Date } {
  const now = new Date();
  switch (period) {
    case "hoje":
      return { from: startOfDay(now), to: now };
    case "ontem": {
      const y = subDays(now, 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case "6h":
      return { from: subHours(now, 6), to: now };
    case "12h":
      return { from: subHours(now, 12), to: now };
    case "24h":
      return { from: subHours(now, 24), to: now };
    case "3d":
      return { from: subDays(now, 3), to: now };
    case "7d":
      return { from: subDays(now, 7), to: now };
    case "custom": {
      const from = custom.fromDate
        ? new Date(`${custom.fromDate}T${custom.fromTime || "00:00"}:00`)
        : subDays(now, 1);
      const to = custom.toDate ? new Date(`${custom.toDate}T${custom.toTime || "23:59"}:00`) : now;
      return { from, to };
    }
  }
}

// ============================================================================
// Helpers de formatação
// ============================================================================

function formatDuration(ms: number): string {
  if (!ms || ms < 0) return "0min";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${m > 0 ? ` ${m}min` : ""}`;
}

function formatDateTime(iso: string): string {
  try {
    return format(new Date(iso), "dd/MM/yyyy HH:mm:ss");
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  try {
    return format(new Date(iso), "HH:mm:ss");
  } catch {
    return iso;
  }
}

// Distância de um trecho (ponto a ponto), não o odômetro acumulado do
// veículo — abaixo de 1 km mostra em metros para ficar legível (ex.: "125 m"
// em vez de "0.1 km").
function formatSegmentDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2).replace(".", ",")} km`;
}

// Cor/espessura/transparência do trajeto — fixas, não configuráveis pelo
// usuário (só as opções de exibição no painel de configurações são).
const ROUTE_COLOR = "#dc2626";
// No app o histórico é sempre azul, seja qual for a cor da empresa (pedido
// 2026-10-30): traçado e botões, sobrescrevendo --primary só nesta tela.
const APP_BLUE = "#2563eb";
const ROUTE_WEIGHT = 4;
const ROUTE_OPACITY = 0.85;

const EVENT_LABELS: Record<string, string> = {
  deviceOnline: "Online",
  deviceOffline: "Offline",
  deviceMoving: "Em movimento",
  deviceStopped: "Parado",
  deviceOverspeed: "Excesso de velocidade",
  geofenceEnter: "Entrou na geocerca",
  geofenceExit: "Saiu da geocerca",
  alarm: "Alarme",
  ignitionOn: "Ignição ligada",
  ignitionOff: "Ignição desligada",
  maintenance: "Manutenção",
};

function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

function isSosEvent(e: VehicleEvent | null): boolean {
  if (!e || e.type !== "alarm") return false;
  const alarm = (e.attributes as { alarm?: string } | undefined)?.alarm;
  return alarm === "sos" || alarm === "sosAlarm";
}

// Uma "parada" só é real quando tem coordenada válida — o Traccar às vezes
// devolve um registro de fronteira (antes do primeiro movimento no período,
// ou depois do último) com lat/lng zerado, que não é uma parada de verdade.
function isRealStop(s: RouteStop): boolean {
  return isValidCoordinate(s.lat, s.lng) && s.durationMs > 0;
}

// ============================================================================
// Colunas da tabela
// ============================================================================

interface RowExtra {
  stopMs: number | null;
  movingMs: number | null;
  event: VehicleEvent | null;
  geofenceNames: string;
  // Distância até a posição anterior (haversine), não o odômetro acumulado
  // do Traccar — null na primeira posição, que não tem "anterior".
  segmentKm: number | null;
}

type ColumnKey =
  | "time"
  | "lat"
  | "lng"
  | "speed"
  | "course"
  | "altitude"
  | "satellites"
  | "accuracy"
  | "address"
  | "event"
  | "ignition"
  | "motion"
  | "stopped"
  | "moving"
  | "segmentDistance"
  | "odometer"
  | "stopTime"
  | "movingTime"
  | "power"
  | "battery"
  | "geofence"
  | "blocked"
  | "sos";

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultVisible: boolean;
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: "time", label: "Hora", defaultVisible: true },
  { key: "lat", label: "Latitude", defaultVisible: true },
  { key: "lng", label: "Longitude", defaultVisible: true },
  { key: "speed", label: "Velocidade", defaultVisible: true },
  { key: "course", label: "Curso", defaultVisible: true },
  { key: "altitude", label: "Altitude", defaultVisible: true },
  { key: "satellites", label: "Satélites", defaultVisible: true },
  { key: "accuracy", label: "Precisão", defaultVisible: true },
  { key: "address", label: "Endereço", defaultVisible: true },
  { key: "event", label: "Evento", defaultVisible: true },
  { key: "ignition", label: "Ignição", defaultVisible: true },
  { key: "motion", label: "Motor", defaultVisible: true },
  { key: "moving", label: "Movimento", defaultVisible: true },
  { key: "stopped", label: "Parado", defaultVisible: false },
  { key: "segmentDistance", label: "Distância do trecho", defaultVisible: true },
  { key: "odometer", label: "Hodômetro", defaultVisible: true },
  { key: "stopTime", label: "Tempo parado", defaultVisible: false },
  { key: "movingTime", label: "Tempo em movimento", defaultVisible: false },
  { key: "power", label: "Alimentação", defaultVisible: true },
  { key: "battery", label: "Bateria", defaultVisible: true },
  { key: "geofence", label: "Geocerca", defaultVisible: true },
  { key: "blocked", label: "Bloqueio", defaultVisible: true },
  { key: "sos", label: "SOS", defaultVisible: true },
];

function cellValue(p: DetailedPosition, key: ColumnKey, extra: RowExtra): string {
  const attrs = p.attributes ?? {};
  switch (key) {
    case "time":
      return formatDateTime(p.timestamp);
    case "lat":
      return p.lat.toFixed(6);
    case "lng":
      return p.lng.toFixed(6);
    case "speed":
      return `${p.speed} km/h`;
    case "course":
      return `${Math.round(p.course)}°`;
    case "altitude":
      return `${Math.round(p.altitude)} m`;
    case "satellites":
      return p.satellites != null ? String(p.satellites) : "—";
    case "accuracy":
      return p.accuracy != null ? `${Math.round(p.accuracy)} m` : "—";
    case "address":
      return p.address ?? "—";
    case "event":
      return extra.event ? describeEvent(extra.event) : "—";
    case "ignition":
      return p.ignition == null ? "—" : p.ignition ? "Ligada" : "Desligada";
    case "motion":
      return p.motion == null ? "—" : p.motion ? "Em movimento" : "Parado";
    case "stopped":
      return p.motion === false ? "Sim" : p.motion === true ? "Não" : "—";
    case "moving":
      return p.motion === true ? "Sim" : p.motion === false ? "Não" : "—";
    case "segmentDistance":
      return extra.segmentKm != null ? formatSegmentDistance(extra.segmentKm) : "—";
    case "odometer":
      return p.totalDistance != null ? `${(p.totalDistance / 1000).toFixed(1)} km` : "—";
    case "stopTime":
      return extra.stopMs != null ? formatDuration(extra.stopMs) : "—";
    case "movingTime":
      return extra.movingMs != null ? formatDuration(extra.movingMs) : "—";
    case "power":
      return typeof attrs.power === "number" ? `${attrs.power} V` : "—";
    case "battery":
      // Traccar manda o nível da bateria interna do rastreador em
      // "batteryLevel" (%, 0-100) — "battery" (Volts) é outro atributo,
      // raramente enviado pelos protocolos em uso nesta frota; conferido
      // direto no banco (tc_positions.attributes) que só "batteryLevel"
      // aparece de fato.
      return typeof attrs.batteryLevel === "number" ? `${attrs.batteryLevel}%` : "—";
    case "geofence":
      return extra.geofenceNames || "—";
    case "blocked":
      return typeof attrs.blocked === "boolean" ? (attrs.blocked ? "Sim" : "Não") : "—";
    case "sos":
      return isSosEvent(extra.event) ? "SOS" : "—";
  }
}

// ============================================================================
// Exportações — CSV (real, escapado corretamente, sem HTML/libs),
// Excel (várias abas, gerado no cliente a partir de dados reais já
// buscados — o Traccar não tem um endpoint que combine resumo/eventos/
// posições/paradas num único arquivo, então montamos o workbook aqui,
// sem inventar nenhum dado).
// ============================================================================

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Escapa um campo para CSV de verdade (RFC 4180: aspas duplicadas, não
// escapadas por barra invertida como o JSON.stringify faz) — sem HTML, sem
// caracteres estranhos.
function csvField(value: string): string {
  const needsQuoting = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
}

function exportCsv(
  positions: DetailedPosition[],
  visible: ColumnKey[],
  extras: RowExtra[],
  filename: string,
) {
  const cols = COLUMN_DEFS.filter((c) => visible.includes(c.key));
  const header = cols.map((c) => csvField(c.label)).join(",");
  const rows = positions.map((p, i) =>
    cols.map((c) => csvField(cellValue(p, c.key, extras[i]))).join(","),
  );
  const csv = [header, ...rows].join("\r\n");
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), filename);
}

async function exportExcelWorkbook(opts: {
  vehicle: Vehicle;
  range: { from: string; to: string };
  summary?: { distanceKm: number; averageSpeed: number; maxSpeed: number };
  movingMs: number;
  stoppedMs: number;
  positions: DetailedPosition[];
  extras: RowExtra[];
  visibleColumns: ColumnKey[];
  stops: RouteStop[];
  events: VehicleEvent[];
  powerCuts: PowerCut[];
  filename: string;
}) {
  const XLSX = await loadXlsx();
  const wb = XLSX.utils.book_new();

  const summaryRows = [
    ["Veículo", opts.vehicle.name],
    ["Placa", opts.vehicle.plate],
    ["Período", `${formatDateTime(opts.range.from)} — ${formatDateTime(opts.range.to)}`],
    ["Total de posições", opts.positions.length],
    ["Distância (km)", (opts.summary?.distanceKm ?? 0).toFixed(1)],
    ["Velocidade média (km/h)", opts.summary?.averageSpeed ?? 0],
    ["Velocidade máxima (km/h)", opts.summary?.maxSpeed ?? 0],
    ["Tempo em movimento", formatDuration(opts.movingMs)],
    ["Tempo parado", formatDuration(opts.stoppedMs)],
    ["Quantidade de paradas", opts.stops.filter(isRealStop).length],
    ["Quantidade de eventos", opts.events.length],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Resumo");

  const eventRows = [
    ["Hora", "Tipo", "Descrição"],
    ...opts.events.map((e) => [formatDateTime(e.eventTime), e.type, describeEvent(e)]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(eventRows), "Eventos");

  const cols = COLUMN_DEFS.filter((c) => opts.visibleColumns.includes(c.key));
  const positionRows = [
    cols.map((c) => c.label),
    ...opts.positions.map((p, i) => cols.map((c) => cellValue(p, c.key, opts.extras[i]))),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(positionRows), "Posições");

  const stopRows = [
    ["Início", "Fim", "Duração", "Endereço", "Hodômetro (km)"],
    ...opts.stops
      .filter(isRealStop)
      .map((s) => [
        formatDateTime(s.startTime),
        formatDateTime(s.endTime),
        formatDuration(s.durationMs),
        s.address ?? "—",
        s.endOdometerKm.toFixed(1),
      ]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(stopRows), "Paradas");

  const powerRows: (string | number)[][] =
    opts.powerCuts.length === 0
      ? [["Não houve corte de energia no período"]]
      : [
          ["Hora do corte", "Energia voltou", "Tempo sem energia", "Tipo", "Local"],
          ...opts.powerCuts.map((c) => [
            powerCutWhen(c),
            c.endTime ? formatDateTime(c.endTime) : "Não voltou no período",
            c.durationMs != null ? formatDuration(c.durationMs) : "—",
            c.label,
            c.address || (c.lat != null && c.lng != null ? `${c.lat}, ${c.lng}` : "—"),
          ]),
        ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(powerRows), "Energia");

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadBlob(new Blob([buf], { type: "application/octet-stream" }), opts.filename);
}

function MapUnavailableMessage({ reason }: { reason?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
      <span className="text-2xl">🗺️⚠️</span>
      <p>Não foi possível carregar o mapa.</p>
      <p className="text-xs">
        Verifique, na chave do Google Maps (Google Cloud Console → Credenciais): se o domínio atual
        está autorizado em "HTTP referrers", se a "Maps JavaScript API" está habilitada para o
        projeto, e se o faturamento (billing) da conta está ativo
        {reason ? ` — erro reportado: "${reason}"` : ""}. A tabela, o player e as exportações
        continuam funcionando normalmente.
      </p>
    </div>
  );
}

// Isola falhas do Google Maps (ex.: chave de API sem o domínio de produção
// autorizado em "HTTP referrers" no Google Cloud Console — isso faz o
// AdvancedMarker do Maps quebrar internamente com "Cannot read properties of
// undefined (reading 'getRootNode'/'get')" assim que uma posição é atribuída
// a um marcador) para que só o mapa mostre uma mensagem amigável, em vez de
// derrubar a tela inteira do Centro de Histórico (tabela/player/exportações
// continuam funcionando normalmente mesmo com o mapa indisponível).
class MapErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error("[RouteHistoryCenter] Falha ao renderizar o mapa:", error);
  }
  render() {
    if (this.state.error) return <MapUnavailableMessage reason={this.state.error.message} />;
    return this.props.children;
  }
}

// ============================================================================
// Componente principal
// ============================================================================

export function RouteHistoryCenter({
  vehicleId,
  vehicles,
  onClose,
  compact = false,
}: {
  vehicleId: string | null;
  vehicles: Vehicle[];
  onClose: () => void;
  // Visual dedicado para o app do cliente (PWA) — pedido 2026-08-29: o
  // layout original (grade de 7 estatísticas, tabela densa de várias
  // colunas, popovers pequenos) foi desenhado para desktop e ficava
  // espremido/ilegível num celular. Em vez de duplicar o componente
  // inteiro, este layout alternativo reaproveita os MESMOS hooks/estado —
  // só a apresentação muda (grade de estatísticas vira faixa rolável,
  // filtro de período vira chips numa linha só, mapa/lista viram abas em
  // tela cheia em vez de divididos 50/50, e a lista de posições vira
  // cartões em vez da tabela densa).
  compact?: boolean;
}) {
  const vehicle = vehicles.find((v) => v.id === vehicleId) ?? null;

  const [period, setPeriod] = useState<Period>("hoje");
  const [customRange, setCustomRange] = useState<CustomRange>({
    fromDate: "",
    fromTime: "",
    toDate: "",
    toTime: "",
  });
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  // Busca automaticamente ao abrir, com o período padrão "hoje".
  useEffect(() => {
    if (!vehicleId) return;
    const r = periodRange("hoje", customRange);
    setPeriod("hoje");
    setRange({ from: r.from.toISOString(), to: r.to.toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const handleSearch = () => {
    const r = periodRange(period, customRange);
    setRange({ from: r.from.toISOString(), to: r.to.toISOString() });
  };

  const enabled = !!vehicleId && !!range;
  const positionsQuery = useVehicleRoutePositions(
    vehicleId ?? undefined,
    range?.from ?? "",
    range?.to ?? "",
    enabled,
  );
  const tripsQuery = useVehicleTrips(
    vehicleId ?? undefined,
    range?.from ?? "",
    range?.to ?? "",
    enabled,
  );
  const stopsQuery = useVehicleStops(
    vehicleId ?? undefined,
    range?.from ?? "",
    range?.to ?? "",
    enabled,
  );
  const summaryQuery = useVehicleSummary(
    vehicleId ?? undefined,
    range?.from ?? "",
    range?.to ?? "",
    enabled,
  );
  const eventsQuery = useVehicleEvents(
    vehicleId ?? undefined,
    range?.from ?? "",
    range?.to ?? "",
    enabled,
  );
  const { data: geofences = [] } = useGeofences(enabled);

  // Camada de validação: nenhuma posição inválida (coordenada fora de
  // intervalo, 0/0, NaN/Infinity) ou envolvida num salto fisicamente
  // impossível chega a ser usada por mapa, player, tabela ou exportações —
  // ver src/lib/position-validation.ts.
  const rawPositions = positionsQuery.data ?? [];
  const { valid: validPositions } = useMemo(
    () => filterValidPositions(rawPositions),
    [rawPositions],
  );

  // "Apagar histórico" (pedido 2026-09-08) — nunca deixa reaparecer no
  // mapa/tabela/player/export um período que o próprio cliente marcou
  // como apagado (nem por um Administrador que restaurou de outro
  // veículo por engano). O Traccar continua devolvendo tudo; a ocultação
  // é só aqui, client-side. Ver history-deletion-filter.ts.
  const { data: historyDeletions } = useHistoryDeletions(vehicleId ? Number(vehicleId) : undefined);
  const filteredPositions = useMemo(
    () => filterDeletedPositions(validPositions, historyDeletions),
    [validPositions, historyDeletions],
  );

  // Endereço: usa o que já vier pronto na posição (nunca sobrescreve dado
  // real) e só geocodifica em lote, por cluster de pontos próximos, o que
  // realmente falta — nunca 1 chamada por posição. Ver
  // src/hooks/use-reverse-geocode.ts.
  const datasetKey = `${vehicleId ?? ""}|${range?.from ?? ""}|${range?.to ?? ""}`;
  const geocodableFixes = useMemo(
    () => filteredPositions.map((p) => ({ id: p.id, lat: p.lat, lng: p.lng, address: p.address })),
    [filteredPositions],
  );
  const resolvedAddresses = useBatchReverseGeocode(geocodableFixes, datasetKey);
  const positions = useMemo(
    () =>
      resolvedAddresses.size === 0
        ? filteredPositions
        : filteredPositions.map((p) =>
            p.address || !resolvedAddresses.has(p.id)
              ? p
              : { ...p, address: resolvedAddresses.get(p.id)! },
          ),
    [filteredPositions, resolvedAddresses],
  );

  const events = eventsQuery.data ?? [];
  const loading = positionsQuery.isFetching || summaryQuery.isFetching;

  // Índice de posição por id — usado para casar eventos (só trazem
  // positionId) com lat/lng reais, sem chamada extra por evento.
  const positionById = useMemo(() => {
    const map = new Map<number, DetailedPosition>();
    for (const p of positions) map.set(p.id, p);
    return map;
  }, [positions]);

  const eventByPositionId = useMemo(() => {
    const map = new Map<number, VehicleEvent>();
    for (const e of events) {
      const pid = (e.attributes as { positionId?: number } | undefined)?.positionId;
      if (typeof pid === "number") map.set(pid, e);
    }
    return map;
  }, [events]);

  const geofenceById = useMemo(() => {
    const map = new Map<number, string>();
    for (const g of geofences) map.set(g.id, g.name);
    return map;
  }, [geofences]);

  // Tempo parado/em movimento por linha — comparando com a posição anterior
  // do mesmo estado de "motion" (o Traccar não fornece isso por posição).
  const rowExtras = useMemo<RowExtra[]>(() => {
    const extras: RowExtra[] = [];
    let stateStart: number | null = null;
    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      const prev = positions[i - 1];
      if (!prev || prev.motion !== p.motion) {
        stateStart = new Date(p.timestamp).getTime();
      }
      const elapsed = stateStart != null ? new Date(p.timestamp).getTime() - stateStart : null;
      const geofenceNames = (p.geofenceIds ?? [])
        .map((id) => geofenceById.get(id))
        .filter(Boolean)
        .join(", ");
      const segmentKm = prev
        ? haversineKm({ lat: prev.lat, lng: prev.lng }, { lat: p.lat, lng: p.lng })
        : null;
      extras.push({
        stopMs: p.motion === false ? elapsed : null,
        movingMs: p.motion === true ? elapsed : null,
        event: eventByPositionId.get(p.id) ?? null,
        geofenceNames,
        segmentKm,
      });
    }
    return extras;
  }, [positions, eventByPositionId, geofenceById]);

  // Estatísticas do cabeçalho — sempre dos relatórios oficiais do Traccar,
  // nunca recalculadas manualmente a partir das posições cruas.
  const summary = summaryQuery.data?.[0];
  const stops = stopsQuery.data ?? [];
  const validStops = useMemo(() => stops.filter(isRealStop), [stops]);
  const trips = tripsQuery.data ?? [];
  const movingMs = trips.reduce((s, t) => s + t.durationMs, 0);
  const stoppedMs = validStops.reduce((s, s2) => s + s2.durationMs, 0);
  // Cortes de energia do período (alarmes do rastreador) — destaque no PDF
  // e no cabeçalho. Ver src/lib/power-cut-detection.ts.
  const powerCuts = useMemo(
    () => detectPowerCuts(positions, eventsQuery.data ?? []),
    [positions, eventsQuery.data],
  );
  // PDF com Google Maps (pedido 2026-10-07 "quero o mapa do Google Maps"):
  // a Static Maps API dá 403 na chave, então no clique montamos um mapa
  // Google JS de verdade (raster, imprime) dentro do relatório, esperamos
  // os tiles carregarem (onPrintMapReady) e aí imprimimos. Sem chave ou com
  // a chave recusada, cai no mapa OSM e imprime direto.
  const [preparingPdf, setPreparingPdf] = useState(false);
  const [printMapReady, setPrintMapReady] = useState(false);

  // Colunas visíveis
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(
    COLUMN_DEFS.filter((c) => c.defaultVisible).map((c) => c.key),
  );
  const toggleColumn = (key: ColumnKey) =>
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  // Painel da tabela (lado direito) — recolhível para o mapa ocupar a tela
  // toda; aberto por padrão. Só usado no layout desktop (!compact) — o
  // compacto usa `compactView` (abas Mapa/Lista) abaixo.
  const [tableOpen, setTableOpen] = useState(true);
  const [compactView, setCompactView] = useState<"mapa" | "lista">("mapa");
  // Pedido 2026-08-29 ("o mapa mal aparece, quero uma linha pra ampliar o
  // mapa") — no compacto, os controles do player + cards ficam recolhidos
  // por padrão (só uma barra fina com play/pause + status), deixando o
  // mapa ocupar quase a tela toda; o botão da barra abre/fecha o resto.
  const [compactPlayerOpen, setCompactPlayerOpen] = useState(false);

  // Configuração do trajeto no mapa — cor/espessura/transparência fixas
  // (não são mais configuráveis pelo usuário, só as opções de exibição
  // abaixo).
  const [showPoints, setShowPoints] = useState(false);
  const [onlyStartEnd, setOnlyStartEnd] = useState(false);
  const [showStops, setShowStops] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  // Desligado por padrão: "seguir veículo" é uma ação explícita do usuário
  // (botão), não um comportamento automático do playback — ver comentário
  // em CenterOnPlayback/FitBounds abaixo sobre a separação entre
  // enquadramento inicial (sempre roda) e acompanhamento contínuo (só
  // quando o usuário liga).
  const [autoCenter, setAutoCenter] = useState(false);
  // Identidade estável (useCallback) — passada pra CenterOnPlayback, que
  // usa isso numa dependência de efeito (listener nativo de wheel/
  // pointerdown). Uma arrow function inline aqui seria recriada a cada
  // render de RouteHistoryCenter, e o player re-renderiza a cada ~33ms
  // durante a reprodução (ver lastCommitRef abaixo) — sem isso, o efeito
  // removeria e re-adicionaria os listeners no <div> do mapa ~30x/s
  // enquanto o player roda, à toa.
  const handleManualCameraChange = useCallback(() => setAutoCenter(false), []);

  // ---------------------------------------------------------------------
  // Player de reprodução — "relógio" virtual (ms desde a primeira posição)
  // avançado por requestAnimationFrame, com a posição do marcador
  // interpolada continuamente entre os dois pontos reais mais próximos
  // (nunca inventa coordenadas fora do trajeto real, só desliza entre
  // dois fixes reais, proporcional ao tempo real entre eles).
  // ---------------------------------------------------------------------
  const t0 = positions.length ? new Date(positions[0].timestamp).getTime() : 0;
  const totalMs =
    positions.length > 1 ? new Date(positions[positions.length - 1].timestamp).getTime() - t0 : 0;

  const [playClock, setPlayClock] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number | null>(null);
  const lastCommitRef = useRef(0);

  useEffect(() => {
    setPlayClock(0);
    setPlaying(false);
  }, [positions.length, t0]);

  useEffect(() => {
    if (!playing) {
      lastFrameRef.current = null;
      return;
    }
    const step = (ts: number) => {
      if (lastFrameRef.current == null) lastFrameRef.current = ts;
      const dt = ts - lastFrameRef.current;
      lastFrameRef.current = ts;
      // Só confirma o estado a ~30fps (throttle) — o cálculo usa o delta de
      // tempo real, não o número de frames, então a suavidade não depende
      // da frequência do commit, só reduz a carga de re-render.
      if (ts - lastCommitRef.current >= 33) {
        lastCommitRef.current = ts;
        setPlayClock((c) => {
          const next = c + dt * playSpeed;
          if (next >= totalMs) {
            setPlaying(false);
            return totalMs;
          }
          return next;
        });
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, playSpeed, totalMs]);

  const sample = useMemo(() => {
    if (positions.length === 0) return null;
    if (positions.length === 1) return { lat: positions[0].lat, lng: positions[0].lng, index: 0 };
    const target = t0 + playClock;
    let i = 0;
    while (i < positions.length - 2 && new Date(positions[i + 1].timestamp).getTime() < target) i++;
    const a = positions[i];
    const b = positions[i + 1];
    const ta = new Date(a.timestamp).getTime();
    const tb = new Date(b.timestamp).getTime();
    const frac = tb > ta ? Math.min(1, Math.max(0, (target - ta) / (tb - ta))) : 0;
    return {
      lat: a.lat + (b.lat - a.lat) * frac,
      lng: a.lng + (b.lng - a.lng) * frac,
      index: frac < 0.5 ? i : i + 1,
    };
  }, [positions, playClock, t0]);

  const playIndex = sample?.index ?? 0;
  const currentPosition = positions[playIndex];
  // Memoizado pelas coordenadas em si (não pelo objeto `sample`, que é uma
  // referência nova a cada render) — sem isso, `currentLatLng` virava um
  // objeto novo em QUALQUER re-render do componente (ex.: um cluster de
  // endereço resolvendo em segundo plano, nada a ver com o player), o que
  // disparava de novo o efeito de recentralização em `CenterOnPlayback`
  // (dependência `[currentLatLng]`) e chamava `map.setCenter()` até com o
  // player pausado — brigando com um zoom que o usuário estivesse tentando
  // dar bem naquele instante.
  const sampleLat = sample?.lat;
  const sampleLng = sample?.lng;
  const currentLatLng = useMemo(
    () => (sampleLat != null && sampleLng != null ? { lat: sampleLat, lng: sampleLng } : null),
    [sampleLat, sampleLng],
  );

  const jumpTo = (index: number) => {
    if (positions.length === 0) return;
    const clamped = Math.max(0, Math.min(positions.length - 1, index));
    setPlayClock(new Date(positions[clamped].timestamp).getTime() - t0);
    setPlaying(false);
  };

  // Linha selecionada na tabela (clique) — centraliza e destaca no mapa.
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const highlightPosition = selectedRowIndex != null ? positions[selectedRowIndex] : undefined;

  // Mapa próprio (independente do mapa principal, que continua em Leaflet)
  // — Google Maps real via @vis.gl/react-google-maps. Import estático (a
  // lib é SSR-safe, diferente do Leaflet).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  const googleMapsApiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  const mapAuthFailed = useGoogleMapsAuthFailure();
  const useGooglePrintMap = !!googleMapsApiKey && !mapAuthFailed;
  const pdfPrintedRef = useRef(false);
  const onPrintMapReady = useCallback(() => setPrintMapReady(true), []);
  useEffect(() => {
    if (!preparingPdf) {
      setPrintMapReady(false);
      pdfPrintedRef.current = false;
      return;
    }
    const go = () => {
      if (pdfPrintedRef.current) return;
      pdfPrintedRef.current = true;
      printHistoryReport(() => setPreparingPdf(false));
    };
    if (!useGooglePrintMap || printMapReady) {
      go();
      return;
    }
    // Mapa travado não segura o PDF pra sempre.
    const t = setTimeout(go, 15_000);
    return () => clearTimeout(t);
  }, [preparingPdf, printMapReady, useGooglePrintMap]);

  // ---------------------------------------------------------------------
  // Virtualização da tabela — não renderiza as 50 mil linhas de uma vez,
  // só a fatia visível + uma margem, com espaçadores no topo/embaixo para
  // manter o tamanho real da barra de rolagem.
  // ---------------------------------------------------------------------
  const ROW_HEIGHT = compact ? 60 : 30;
  const OVERSCAN = 12;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(400);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewportHeight(el.clientHeight));
    ro.observe(el);
    setViewportHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  const visibleCols = useMemo(
    () => COLUMN_DEFS.filter((c) => visibleColumns.includes(c.key)),
    [visibleColumns],
  );
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    positions.length,
    startIndex + Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2,
  );
  const topPad = startIndex * ROW_HEIGHT;
  const bottomPad = Math.max(0, (positions.length - endIndex) * ROW_HEIGHT);

  if (!vehicleId || !vehicle) return null;

  const columnPickerContent = (
    <div className="grid max-h-80 grid-cols-2 gap-x-4 gap-y-1.5 overflow-y-auto">
      {COLUMN_DEFS.map((c) => (
        <label key={c.key} className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={visibleColumns.includes(c.key)}
            onCheckedChange={() => toggleColumn(c.key)}
          />
          {c.label}
        </label>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[1200] flex flex-col bg-background"
      style={compact ? ({ "--primary": APP_BLUE, "--primary-foreground": "#ffffff", "--ring": APP_BLUE } as CSSProperties) : undefined}
    >
      <div className="wp-screen-only flex flex-1 flex-col overflow-hidden">
        {/* Cabeçalho */}
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-4 border-b border-border bg-gradient-to-b from-card to-card/80 shadow-sm",
            compact ? "gap-2 px-4 pb-3" : "px-5 py-4",
          )}
          // Só no modo compacto (PWA/Android): soma a safe-area do topo ao
          // padding de sempre — o desktop nunca roda dentro do WebView do
          // Capacitor, então não precisa disso (mesmo motivo do header do
          // Início em mobile-app.tsx).
          style={compact ? { paddingTop: "calc(0.75rem + env(safe-area-inset-top))" } : undefined}
        >
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Fechar"
              className="shrink-0 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-lg leading-none">🚗</span>
                <h1 className="truncate text-base font-bold tracking-tight text-foreground">
                  {vehicle.name}
                </h1>
                <LicensePlate plate={vehicle.plate} size="sm" />
              </div>
              {!compact && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <span>📅</span>
                  {range
                    ? `${formatDateTime(range.from)} — ${formatDateTime(range.to)}`
                    : "Selecione um período"}
                </p>
              )}
            </div>
          </div>
          <div
            className={cn(
              compact
                ? "flex w-full gap-2 overflow-x-auto pb-0.5"
                : "grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-8",
            )}
          >
            <Stat emoji="📍" label="Posições" value={String(positions.length)} />
            <Stat
              emoji="📏"
              label="Distância"
              value={`${(summary?.distanceKm ?? 0).toFixed(1)} km`}
            />
            <Stat emoji="🚶" label="Em movimento" value={formatDuration(movingMs)} />
            <Stat emoji="🅿" label="Parado" value={formatDuration(stoppedMs)} />
            <Stat emoji="🚀" label="Vel. máxima" value={`${summary?.maxSpeed ?? 0} km/h`} />
            <Stat emoji="📈" label="Vel. média" value={`${summary?.averageSpeed ?? 0} km/h`} />
            <Stat emoji="🛑" label="Paradas" value={String(validStops.length)} />
            <Stat
              emoji={powerCuts.length > 0 ? "⚡" : "🔋"}
              label="Energia"
              value={
                eventsQuery.isFetching
                  ? "…"
                  : powerCuts.length === 0
                    ? "Sem cortes"
                    : `${powerCuts.length} corte${powerCuts.length > 1 ? "s" : ""} · ${formatTime(powerCuts[0].startTime)}`
              }
              tone={powerCuts.length > 0 ? "danger" : "ok"}
            />
          </div>
        </div>

        {/* Filtro de período — no compacto, os chips ficam numa faixa rolável
            própria e o período personalizado/botão Pesquisar vão numa
            segunda linha, sem disputar espaço horizontal com os chips. */}
        <div className={cn("border-b border-border bg-card/60 px-4 py-2", compact && "space-y-2")}>
          <div className={cn("flex items-center gap-2", compact ? "overflow-x-auto" : "flex-wrap")}>
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPeriod(opt.key)}
                className={cn(
                  "shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                  period === opt.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-accent",
                )}
              >
                {opt.label}
              </button>
            ))}
            {!compact && period === "custom" && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <input
                  type="date"
                  value={customRange.fromDate}
                  onChange={(e) => setCustomRange((c) => ({ ...c, fromDate: e.target.value }))}
                  className="rounded-md border border-border bg-background px-2 py-1"
                />
                <input
                  type="time"
                  value={customRange.fromTime}
                  onChange={(e) => setCustomRange((c) => ({ ...c, fromTime: e.target.value }))}
                  className="rounded-md border border-border bg-background px-2 py-1"
                />
                <span className="text-muted-foreground">até</span>
                <input
                  type="date"
                  value={customRange.toDate}
                  onChange={(e) => setCustomRange((c) => ({ ...c, toDate: e.target.value }))}
                  className="rounded-md border border-border bg-background px-2 py-1"
                />
                <input
                  type="time"
                  value={customRange.toTime}
                  onChange={(e) => setCustomRange((c) => ({ ...c, toTime: e.target.value }))}
                  className="rounded-md border border-border bg-background px-2 py-1"
                />
              </div>
            )}
            {!compact && (
              <Button size="sm" onClick={handleSearch} disabled={loading} className="ml-auto gap-1.5">
                <Search className="h-3.5 w-3.5" />
                {loading ? "Buscando…" : "Pesquisar"}
              </Button>
            )}
          </div>

          {compact && period === "custom" && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <input
                type="date"
                value={customRange.fromDate}
                onChange={(e) => setCustomRange((c) => ({ ...c, fromDate: e.target.value }))}
                className="rounded-md border border-border bg-background px-2 py-1"
              />
              <input
                type="time"
                value={customRange.fromTime}
                onChange={(e) => setCustomRange((c) => ({ ...c, fromTime: e.target.value }))}
                className="rounded-md border border-border bg-background px-2 py-1"
              />
              <span className="text-muted-foreground">até</span>
              <input
                type="date"
                value={customRange.toDate}
                onChange={(e) => setCustomRange((c) => ({ ...c, toDate: e.target.value }))}
                className="rounded-md border border-border bg-background px-2 py-1"
              />
              <input
                type="time"
                value={customRange.toTime}
                onChange={(e) => setCustomRange((c) => ({ ...c, toTime: e.target.value }))}
                className="rounded-md border border-border bg-background px-2 py-1"
              />
            </div>
          )}
          {compact && (
            <Button size="sm" onClick={handleSearch} disabled={loading} className="w-full gap-1.5">
              <Search className="h-3.5 w-3.5" />
              {loading ? "Buscando…" : "Pesquisar"}
            </Button>
          )}
        </div>

        {/* Abas Mapa/Lista — só no compacto, substitui a divisão 50/50 lado a
            lado do desktop por telas cheias alternadas por aba. */}
        {compact && (
          <div className="flex border-b border-border bg-card">
            <button
              type="button"
              onClick={() => setCompactView("mapa")}
              className={cn(
                "flex-1 border-b-2 py-2 text-sm font-semibold transition-colors",
                compactView === "mapa"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground",
              )}
            >
              Mapa
            </button>
            <button
              type="button"
              onClick={() => setCompactView("lista")}
              className={cn(
                "flex-1 border-b-2 py-2 text-sm font-semibold transition-colors",
                compactView === "lista"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground",
              )}
            >
              Lista ({positions.length})
            </button>
          </div>
        )}

        {/* Corpo: mapa + player à esquerda, tabela embaixo (desktop) — no
            compacto, mapa e lista viram abas em tela cheia (compactView),
            então só um dos dois é montado por vez, sem a divisão 50/50. */}
        <div
          className={cn(
            "relative flex flex-1 flex-col overflow-hidden",
            !compact && "lg:flex-row",
          )}
        >
          <div
            className={cn(
              "relative flex flex-1 flex-col",
              compact
                ? compactView !== "mapa" && "hidden"
                : cn(
                    "border-b border-border lg:border-b-0 lg:border-r",
                    tableOpen ? "lg:w-1/2" : "lg:w-full",
                  ),
            )}
          >
            <div className="relative flex-1">
              {googleMapsApiKey && !mapAuthFailed ? (
                <MapErrorBoundary>
                  <HistoryMap
                    apiKey={googleMapsApiKey}
                    mapRef={mapRef}
                    positions={positions}
                    stops={validStops}
                    events={events}
                    positionById={positionById}
                    routeColor={compact ? APP_BLUE : ROUTE_COLOR}
                    routeWeight={ROUTE_WEIGHT}
                    routeOpacity={ROUTE_OPACITY}
                    showPoints={showPoints}
                    onlyStartEnd={onlyStartEnd}
                    showStops={showStops}
                    showEvents={showEvents}
                    currentLatLng={currentLatLng}
                    currentPosition={currentPosition}
                    highlightPosition={highlightPosition}
                    autoCenter={autoCenter}
                    onManualCameraChange={handleManualCameraChange}
                  />
                </MapErrorBoundary>
              ) : mapAuthFailed ? (
                <MapUnavailableMessage />
              ) : (
                <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
                  Configure VITE_GOOGLE_MAPS_API_KEY para carregar o mapa.
                </div>
              )}

              {/* Painel de configuração do trajeto */}
              <div className="absolute right-3 top-3 z-[400]">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card/95 text-foreground shadow-lg backdrop-blur hover:bg-accent"
                      aria-label="Configurações do trajeto"
                    >
                      <Settings2 className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="z-[1300] w-72 space-y-3">
                    <div className="text-xs font-bold text-foreground">
                      Configurações do trajeto
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <ConfigToggle
                        label="Mostrar pontos GPS"
                        checked={showPoints}
                        onChange={setShowPoints}
                      />
                      <ConfigToggle
                        label="Mostrar somente início/fim"
                        checked={onlyStartEnd}
                        onChange={setOnlyStartEnd}
                      />
                      <ConfigToggle
                        label="Mostrar paradas"
                        checked={showStops}
                        onChange={setShowStops}
                      />
                      <ConfigToggle
                        label="Mostrar eventos"
                        checked={showEvents}
                        onChange={setShowEvents}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <button
                type="button"
                onClick={() => setAutoCenter((v) => !v)}
                className={cn(
                  "absolute bottom-3 right-3 z-[400] flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold shadow-lg backdrop-blur transition-colors",
                  autoCenter
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card/95 text-foreground hover:bg-accent",
                )}
              >
                <Locate className="h-3.5 w-3.5" />
                Centralizar automaticamente
              </button>

              {!compact && !tableOpen && (
                <button
                  type="button"
                  onClick={() => setTableOpen(true)}
                  className="absolute bottom-3 left-3 z-[400] flex items-center gap-1.5 rounded-lg border border-border bg-card/95 px-2.5 py-1.5 text-xs font-semibold text-foreground shadow-lg backdrop-blur transition-colors hover:bg-accent"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Mostrar lista
                </button>
              )}
            </div>

            {/* Player de reprodução — no compacto, recolhido por padrão (só
                uma barrinha com play/pause + status): pedido 2026-08-29 "o
                mapa mal aparece, quero uma linha pra ampliar o mapa". O
                botão da barra abre os controles completos (altura máxima
                própria com rolagem, nunca espreme o mapa — que tem flex-1
                no container pai). */}
            <div className={cn("border-t border-border bg-card", compact ? "px-4 py-2" : "px-4 py-2.5")}>
              {compact && (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPlaying((p) => !p)}
                    disabled={positions.length < 2}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
                    aria-label={playing ? "Pausar" : "Reproduzir"}
                  >
                    {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-bold text-foreground">
                      {currentPosition ? formatTime(currentPosition.timestamp) : "—"}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {positions.length ? `${playIndex + 1} / ${positions.length} posições` : "Sem posições"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCompactPlayerOpen((v) => !v)}
                    className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-semibold text-foreground hover:bg-accent"
                  >
                    {compactPlayerOpen ? (
                      <>
                        Ampliar mapa <ChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Ver controles <ChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                </div>
              )}

              {(!compact || compactPlayerOpen) && (
                <div className={cn(compact && "mt-3 max-h-[32vh] overflow-y-auto")}>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => jumpTo(0)}
                  className="grid h-8 w-8 place-items-center rounded-md border border-border text-foreground hover:bg-accent"
                  aria-label="Primeiro ponto"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => jumpTo(playIndex - 1)}
                  className="grid h-8 w-8 place-items-center rounded-md border border-border text-foreground hover:bg-accent"
                  aria-label="Anterior"
                >
                  <SkipBack className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPlaying((p) => !p)}
                  disabled={positions.length < 2}
                  className="grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                  aria-label={playing ? "Pausar" : "Reproduzir"}
                >
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => jumpTo(playIndex + 1)}
                  className="grid h-8 w-8 place-items-center rounded-md border border-border text-foreground hover:bg-accent"
                  aria-label="Próximo"
                >
                  <SkipForward className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => jumpTo(positions.length - 1)}
                  className="grid h-8 w-8 place-items-center rounded-md border border-border text-foreground hover:bg-accent"
                  aria-label="Último"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
                <div className="ml-1 flex items-center gap-1">
                  {[1, 2, 4, 8, 16].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setPlaySpeed(s)}
                      className={cn(
                        "rounded-md border px-1.5 py-0.5 text-[11px] font-semibold",
                        playSpeed === s
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:bg-accent",
                      )}
                    >
                      {s}x
                    </button>
                  ))}
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, totalMs)}
                  value={playClock}
                  onChange={(e) => {
                    setPlayClock(Number(e.target.value));
                    setPlaying(false);
                  }}
                  disabled={positions.length < 2}
                  className={cn(
                    "h-1.5 accent-primary",
                    compact ? "order-last mt-1.5 w-full basis-full" : "ml-2 flex-1",
                  )}
                />
              </div>
              <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                <span>
                  Tempo atual:{" "}
                  <b className="text-foreground">
                    {currentPosition ? formatTime(currentPosition.timestamp) : "—"}
                  </b>
                </span>
                <span>
                  Posição atual:{" "}
                  <b className="text-foreground">
                    {positions.length ? `${playIndex + 1} / ${positions.length}` : "—"}
                  </b>
                </span>
                <span>
                  Total de posições: <b className="text-foreground">{positions.length}</b>
                </span>
              </div>

              {/* Informações inferiores — cards, atualizando durante o Play.
                  No compacto, só os 4 mais relevantes pra quem só quer ver a
                  posição no app (os outros 8 — satélites/precisão/distância
                  etc. — continuam só no desktop, onde há espaço de sobra). */}
              {currentPosition && compact && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <InfoCard emoji="🕒" label="Hora" value={formatTime(currentPosition.timestamp)} />
                  <InfoCard emoji="🚗" label="Velocidade" value={`${currentPosition.speed} km/h`} />
                  <InfoCard
                    emoji="📍"
                    label="Endereço"
                    value={currentPosition.address ?? "—"}
                    className="col-span-2"
                  />
                  <InfoCard
                    emoji="⚠"
                    label="Evento"
                    value={
                      rowExtras[playIndex]?.event
                        ? describeEvent(rowExtras[playIndex].event!)
                        : "—"
                    }
                    className="col-span-2"
                  />
                </div>
              )}
              {currentPosition && !compact && (
                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                  <InfoCard emoji="🕒" label="Hora" value={formatTime(currentPosition.timestamp)} />
                  <InfoCard emoji="🚗" label="Velocidade" value={`${currentPosition.speed} km/h`} />
                  <InfoCard
                    emoji="🧭"
                    label="Curso"
                    value={`${Math.round(currentPosition.course)}°`}
                  />
                  <InfoCard
                    emoji="🔑"
                    label="Ignição"
                    value={
                      currentPosition.ignition == null
                        ? "—"
                        : currentPosition.ignition
                          ? "Ligada"
                          : "Desligada"
                    }
                  />
                  <InfoCard
                    emoji="⚙️"
                    label="Motor"
                    value={
                      currentPosition.motion == null
                        ? "—"
                        : currentPosition.motion
                          ? "Ligado"
                          : "Desligado"
                    }
                  />
                  <InfoCard
                    emoji="🚶"
                    label="Movimento"
                    value={currentPosition.motion ? "Sim" : "Não"}
                  />
                  <InfoCard
                    emoji="⚠"
                    label="Evento"
                    value={
                      rowExtras[playIndex]?.event
                        ? describeEvent(rowExtras[playIndex].event!)
                        : "—"
                    }
                  />
                  <InfoCard
                    emoji="📍"
                    label="Endereço"
                    value={currentPosition.address ?? "—"}
                    className="col-span-2"
                  />
                  <InfoCard
                    emoji="📡"
                    label="Satélites"
                    value={
                      currentPosition.satellites != null ? String(currentPosition.satellites) : "—"
                    }
                  />
                  <InfoCard
                    emoji="🏔"
                    label="Altitude"
                    value={`${Math.round(currentPosition.altitude)} m`}
                  />
                  <InfoCard
                    emoji="🎯"
                    label="Precisão"
                    value={
                      currentPosition.accuracy != null
                        ? `${Math.round(currentPosition.accuracy)} m`
                        : "—"
                    }
                  />
                  <InfoCard
                    emoji="📏"
                    label="Distância do trecho"
                    value={
                      rowExtras[playIndex]?.segmentKm != null
                        ? formatSegmentDistance(rowExtras[playIndex].segmentKm as number)
                        : "—"
                    }
                  />
                  <InfoCard
                    emoji="🧮"
                    label="Distância acumulada"
                    value={
                      currentPosition.totalDistance != null
                        ? `${(currentPosition.totalDistance / 1000).toFixed(1)} km`
                        : "—"
                    }
                  />
                </div>
              )}
                </div>
              )}
            </div>
          </div>

          {/* Lista de posições — tabela densa (desktop) ou cartões (app,
              compact) reaproveitando a mesma virtualização/dados. */}
          {(compact ? compactView === "lista" : tableOpen) && (
            <div className={cn("flex flex-1 flex-col overflow-hidden bg-card", !compact && "lg:w-1/2")}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-3 py-2.5">
                <div className="flex items-center gap-2">
                  {!compact && (
                    <button
                      type="button"
                      onClick={() => setTableOpen(false)}
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      aria-label="Recolher lista de posições"
                      title="Recolher lista"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                  <div className="leading-tight">
                    <div className="text-xs font-bold text-foreground">Posições</div>
                    <div className="text-[11px] text-muted-foreground">
                      {positions.length} registros
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {!compact && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1.5">
                          <Columns3 className="h-3.5 w-3.5" />
                          Selecionar colunas
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="z-[1300] w-80">
                        {columnPickerContent}
                      </PopoverContent>
                    </Popover>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() =>
                      exportCsv(
                        positions,
                        visibleColumns,
                        rowExtras,
                        `historico-${vehicle.plate}-${Date.now()}.csv`,
                      )
                    }
                    disabled={positions.length === 0}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    {!compact && "CSV"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={positions.length === 0 || !range}
                    onClick={() => {
                      if (!range) return;
                      exportExcelWorkbook({
                        vehicle,
                        range,
                        summary,
                        movingMs,
                        stoppedMs,
                        positions,
                        extras: rowExtras,
                        visibleColumns,
                        stops: validStops,
                        events,
                        powerCuts,
                        filename: `historico-${vehicle.plate}-${Date.now()}.xlsx`,
                      });
                    }}
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    {!compact && "Excel"}
                  </Button>
                  {!compact && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setPreparingPdf(true)}
                      disabled={positions.length === 0 || preparingPdf}
                    >
                      {preparingPdf ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Printer className="h-3.5 w-3.5" />
                      )}
                      PDF
                    </Button>
                  )}
                </div>
              </div>
              <div
                ref={scrollRef}
                className="flex-1 overflow-auto"
                onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
              >
                {compact ? (
                  <div>
                    {topPad > 0 && <div style={{ height: topPad }} />}
                    {positions.slice(startIndex, endIndex).map((p, offset) => {
                      const i = startIndex + offset;
                      const extra = rowExtras[i];
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelectedRowIndex(i);
                            jumpTo(i);
                            setCompactView("mapa");
                          }}
                          style={{ height: ROW_HEIGHT }}
                          className={cn(
                            "flex w-full flex-col justify-center gap-0.5 border-b border-border/60 px-4 py-1.5 text-left transition-colors",
                            selectedRowIndex === i ? "bg-primary/10" : "hover:bg-accent/50",
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold text-foreground">
                              {formatTime(p.timestamp)}
                            </span>
                            <span className="shrink-0 text-xs font-semibold text-foreground">
                              {p.speed} km/h
                            </span>
                          </div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {p.address ?? "—"}
                          </div>
                          <div className="h-4">
                            {extra?.event && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                                ⚠ {describeEvent(extra.event)}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    {bottomPad > 0 && <div style={{ height: bottomPad }} />}
                    {positions.length === 0 && (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        {loading ? "Carregando posições…" : "Nenhuma posição no período selecionado."}
                      </div>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
                      <TableRow className="hover:bg-transparent">
                        {visibleCols.map((c) => (
                          <TableHead
                            key={c.key}
                            className="bg-card text-[11px] font-bold uppercase tracking-wide text-muted-foreground"
                          >
                            {c.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topPad > 0 && (
                        <tr>
                          <td
                            colSpan={Math.max(1, visibleCols.length)}
                            style={{ height: topPad, padding: 0, border: "none" }}
                          />
                        </tr>
                      )}
                      {positions.slice(startIndex, endIndex).map((p, offset) => {
                        const i = startIndex + offset;
                        const extra = rowExtras[i];
                        return (
                          <TableRow
                            key={p.id}
                            onClick={() => {
                              setSelectedRowIndex(i);
                              jumpTo(i);
                            }}
                            style={{ height: ROW_HEIGHT }}
                            className={cn(
                              "cursor-pointer transition-colors",
                              selectedRowIndex === i
                                ? "bg-primary/10 hover:bg-primary/10"
                                : i % 2 === 1 && "bg-muted/30",
                            )}
                          >
                            {visibleCols.map((c) =>
                              c.key === "event" ? (
                                <TableCell key={c.key} className="whitespace-nowrap py-1 text-xs">
                                  {extra?.event ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                                      ⚠ {describeEvent(extra.event)}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground/60">—</span>
                                  )}
                                </TableCell>
                              ) : (
                                <TableCell key={c.key} className="whitespace-nowrap py-1 text-xs">
                                  {cellValue(p, c.key, extra)}
                                </TableCell>
                              ),
                            )}
                          </TableRow>
                        );
                      })}
                      {bottomPad > 0 && (
                        <tr>
                          <td
                            colSpan={Math.max(1, visibleCols.length)}
                            style={{ height: bottomPad, padding: 0, border: "none" }}
                          />
                        </tr>
                      )}
                      {positions.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(1, visibleCols.length)}
                            className="py-8 text-center text-sm text-muted-foreground"
                          >
                            {loading
                              ? "Carregando posições…"
                              : "Nenhuma posição no período selecionado."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Relatório para impressão/PDF — layout próprio, não é a tela. Só
          visível durante impressão (ver estilo global no fim do arquivo).
          Renderizado via portal direto em document.body, fora do wrapper
          "fixed inset-0" deste componente: um <div position:fixed> como
          ancestral é uma causa conhecida de página em branco no
          Chrome/"Salvar como PDF" (o motor de impressão pagina o conteúdo
          errado quando ele está preso a um ancestral fixed) — o portal
          evita esse ancestral por completo. */}
      {range &&
        createPortal(
          <PrintReport
            vehicle={vehicle}
            range={range}
            summary={summary}
            movingMs={movingMs}
            stoppedMs={stoppedMs}
            positions={positions}
            stops={validStops}
            trips={trips}
            events={events}
            powerCuts={powerCuts}
            routeColor={compact ? APP_BLUE : ROUTE_COLOR}
            googleMapsApiKey={useGooglePrintMap ? googleMapsApiKey : null}
            preparing={preparingPdf}
            onPrintMapReady={onPrintMapReady}
          />,
          document.body,
        )}
      <style>{`
        @media screen {
          .wp-print-only { display: none; }
          /* Preparando o PDF: o Google Maps só carrega tiles quando o mapa
             está dentro da área visível (fora da tela ou em display:none ele
             nem começa — testado em Chrome headless 07/10). Então o
             relatório fica na tela, mas transparente e atrás de tudo. */
          .wp-print-only.wp-print-preparing {
            display: block; position: fixed; left: 0; top: 0;
            width: 688px; opacity: 0; z-index: -1; pointer-events: none;
          }
        }
        @media print {
          body * { visibility: hidden; }
          .wp-print-only, .wp-print-only * { visibility: visible; }
          .wp-print-only { display: block; position: absolute; inset: 0; }
          .wp-print-only, .wp-print-only * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4; margin: 14mm; }
        }
        .wp-stop-infowindow { animation: wp-popup-in 180ms cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes wp-popup-in {
          from { opacity: 0; transform: scale(.9) translateY(4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .wp-info-card { animation: wp-card-in 240ms cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes wp-card-in {
          from { opacity: 0.45; transform: translateY(2px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .wp-live-dot { animation: wp-live-pulse 1.6s ease-in-out infinite; }
        @keyframes wp-live-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(37,99,235,.45); }
          50% { box-shadow: 0 0 0 6px rgba(37,99,235,0); }
        }
      `}</style>
    </div>
  );
}

function Stat({
  emoji,
  label,
  value,
  tone,
}: {
  emoji: string;
  label: string;
  value: string;
  tone?: "ok" | "danger";
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-2 rounded-xl border border-border/60 bg-background/60 px-2.5 py-1.5 text-xs",
        tone === "danger" && "border-red-500/60 bg-red-500/10",
        tone === "ok" && "border-emerald-500/40",
      )}
    >
      <span className="text-base leading-none">{emoji}</span>
      <div className="min-w-0">
        <div className="truncate leading-none text-[10px] font-medium text-muted-foreground">
          {label}
        </div>
        <div className="truncate font-bold text-foreground">{value}</div>
      </div>
    </div>
  );
}

function InfoCard({
  emoji,
  label,
  value,
  className,
}: {
  emoji: string;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div
      key={value}
      className={cn(
        "wp-info-card rounded-xl border border-border/70 bg-background px-3 py-2 shadow-sm transition-colors duration-300",
        className,
      )}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="text-sm leading-none">{emoji}</span>
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-bold text-foreground">{value}</div>
    </div>
  );
}

function ConfigToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2">
      <span>{label}</span>
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
    </label>
  );
}

function StopRow({ emoji, label, value }: { emoji: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-base leading-none">{emoji}</span>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </div>
        <div className="truncate text-[13px] font-medium text-white">{value}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Mapa — próprio, independente do mapa principal
// ============================================================================

function Dot({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        border: "2px solid white",
        boxShadow: "0 1px 3px rgba(0,0,0,.5)",
      }}
    />
  );
}

// Marcadores "premium" em emoji (início/fim/parada/evento) — badge branco
// com anel colorido semântico, em vez do antigo ponto colorido genérico.
// Componente de módulo (identidade estável entre renders) — definir isso
// dentro de HistoryMap fazia o React tratar cada frame do player como um
// tipo de componente novo, forçando o AdvancedMarker a desmontar/remontar
// sem necessidade.
function EmojiMarker({ emoji, ring, size = 30 }: { emoji: string; ring: string; size?: number }) {
  return (
    <div
      className="grid place-items-center rounded-full bg-white shadow-lg"
      style={{
        width: size,
        height: size,
        border: `2.5px solid ${ring}`,
        fontSize: size * 0.52,
        lineHeight: 1,
      }}
    >
      {emoji}
    </div>
  );
}

// Marcador + InfoWindow associados via `useAdvancedMarkerRef`/`anchor` (o
// padrão oficial da lib) em vez de aninhar `<InfoWindow>` como filho direto
// de `<AdvancedMarker>` — aninhar dessa forma é uma causa documentada de
// "Cannot read properties of undefined (reading 'getRootNode')" quando o
// marcador é recriado (o InfoWindow some junto com o container antigo antes
// do próprio React terminar de desmontá-lo). Estado de aberto/fechado
// continua compartilhado (só um popup por vez) via `openMarker`/`id`.
function MarkerWithInfo({
  id,
  position,
  zIndex,
  openMarker,
  setOpenMarker,
  info,
  infoClassName,
  children,
}: {
  id: string;
  position: { lat: number; lng: number };
  zIndex?: number;
  openMarker: string | null;
  setOpenMarker: (id: string | null) => void;
  info: React.ReactNode;
  infoClassName?: string;
  children: React.ReactNode;
}) {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const isOpen = openMarker === id;
  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={position}
        onClick={() => setOpenMarker(isOpen ? null : id)}
        zIndex={zIndex}
      >
        {children}
      </AdvancedMarker>
      {isOpen && marker && (
        <InfoWindow
          anchor={marker}
          onCloseClick={() => setOpenMarker(null)}
          className={infoClassName}
        >
          {info}
        </InfoWindow>
      )}
    </>
  );
}

// Extraídos pra fora de HistoryMap de propósito — eram declarados dentro do
// corpo do componente (`function FitBounds() {...}` local), o que recria a
// função (nova identidade) a cada render de HistoryMap. O React compara
// componentes de função por identidade de referência: uma identidade nova na
// mesma posição do JSX é tratada como um tipo diferente, então ele desmonta
// a instância antiga e monta uma nova — resetando `lastCountRef`/
// `suppressUntilRef` (useRef reinicia do zero). Isso reabria a condição
// `positions.length === lastCountRef.current` (virava falsa de novo) e
// disparava fitBounds() de novo, mesmo sem o dataset ter mudado — bug
// relatado como "eu dou zoom e ele volta sozinho": qualquer re-render de
// HistoryMap (ex.: o próprio wheel do usuário setando autoCenter=false no
// componente pai, que rerenderiza HistoryMap com novas props) remontava
// FitBounds e cancelava o zoom que o usuário acabara de dar. Com os
// componentes fora do corpo de HistoryMap, a identidade fica estável entre
// renders e só desmonta de verdade quando HistoryMap inteiro desmonta.
function FitBounds({
  positions,
  mapRef,
  programmaticCameraRef,
}: {
  positions: DetailedPosition[];
  mapRef: { current: google.maps.Map | null };
  programmaticCameraRef: { current: boolean };
}) {
  const map = useGoogleMap();
  const lastCountRef = useRef(0);
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  useEffect(() => {
    if (!map || positions.length < 2 || positions.length === lastCountRef.current) return;
    lastCountRef.current = positions.length;
    const bounds = new google.maps.LatLngBounds();
    for (const p of positions) bounds.extend({ lat: p.lat, lng: p.lng });
    programmaticCameraRef.current = true;
    map.fitBounds(bounds, 60);
    // Rede de segurança: "zoom_changed" (que normalmente consome essa
    // flag) só dispara se o fitBounds() de fato mudar o nível de zoom.
    // Se o novo dataset já cabe no zoom atual (bounds parecidos com o
    // anterior), zoom_changed nunca dispara e a flag ficava travada em
    // "true" para sempre — engolindo silenciosamente o próximo zoom
    // manual de verdade do usuário (onZoomChanged via de baixo assumia
    // que era programático e não desligava o autoCenter), fazendo o
    // player continuar brigando com o zoom até o usuário tentar de novo.
    const clear = setTimeout(() => {
      programmaticCameraRef.current = false;
    }, 500);
    return () => clearTimeout(clear);
    // Depende de `positions.length`, não de `positions` (a referência do
    // array) — `positions` ganha uma referência nova sempre que o
    // geocoding em lote resolve um endereço (mesmo tamanho, conteúdo
    // enriquecido, ver useBatchReverseGeocode), o que reexecutaria este
    // efeito sem o dataset ter mudado de fato. Como o cleanup acima
    // (clearTimeout) roda antes da nova execução, e a nova execução cai no
    // guard "length === lastCountRef" e retorna cedo sem reagendar o
    // timeout, `programmaticCameraRef.current` ficava travado em `true`
    // pra sempre — fazendo o próximo zoom manual real do usuário ser
    // interpretado como programático e ignorado silenciosamente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions.length, map, programmaticCameraRef]);
  return null;
}

// "autoCenter" começa DESLIGADO (useState(false) em RouteHistoryCenter) —
// seguir o veículo é uma ação explícita do botão "Centralizar
// automaticamente" (Locate), não um efeito colateral automático do play. Por
// padrão, dar play só atualiza `currentLatLng`/marcador/timeline; a câmera
// nunca se move sozinha. O enquadramento inicial do trajeto (fitBounds em
// FitBounds, acima) é intencionalmente independente disso — sempre roda ao
// carregar um novo período, ligado ou não o autoCenter.
//
// Quando o usuário liga o botão, passa a centralizar sem animação (evita
// "brigar" com zoom/arraste do usuário — panTo/setCenter a cada frame do
// player entraria em conflito com o gesto/inércia do usuário).
// "autoCenter" desliga sozinho assim que o usuário arrasta OU dá zoom no
// mapa — ver onDragstart/onZoomChanged no <GoogleMap> em HistoryMap
// (setCenter programático não muda o zoom, então não dispara onZoomChanged;
// fitBounds dispara mas é filtrado via programmaticCameraRef).
//
// Ainda existe uma janela de corrida: o player chama setCenter até 30x/s e
// "zoom_changed" só dispara depois que o Maps já processou o gesto — um
// scroll do usuário podia ser cancelado por um setCenter no meio do gesto,
// antes do autoCenter desligar de fato. Por isso ouve wheel/pointerdown
// direto no <div> do mapa (evento nativo, sem esperar o ciclo do React) e já
// suprime o próximo setCenter na hora, além de desligar o autoCenter
// normalmente.
function CenterOnPlayback({
  autoCenter,
  currentLatLng,
  onManualCameraChange,
}: {
  autoCenter: boolean;
  currentLatLng: { lat: number; lng: number } | null;
  onManualCameraChange?: () => void;
}) {
  const map = useGoogleMap();
  const suppressUntilRef = useRef(0);

  useEffect(() => {
    if (!map) return;
    const div = map.getDiv();
    const onUserGesture = () => {
      suppressUntilRef.current = Date.now() + 600;
      onManualCameraChange?.();
    };
    div.addEventListener("wheel", onUserGesture, { passive: true });
    div.addEventListener("pointerdown", onUserGesture, { passive: true });
    return () => {
      div.removeEventListener("wheel", onUserGesture);
      div.removeEventListener("pointerdown", onUserGesture);
    };
  }, [map, onManualCameraChange]);

  useEffect(() => {
    if (!map || !autoCenter || !currentLatLng) return;
    if (Date.now() < suppressUntilRef.current) return;
    map.setCenter({ lat: currentLatLng.lat, lng: currentLatLng.lng });
  }, [autoCenter, currentLatLng, map]);
  return null;
}

function HistoryMap({
  apiKey,
  mapRef,
  positions,
  stops,
  events,
  positionById,
  routeColor,
  routeWeight,
  routeOpacity,
  showPoints,
  onlyStartEnd,
  showStops,
  showEvents,
  currentLatLng,
  currentPosition,
  highlightPosition,
  autoCenter,
  onManualCameraChange,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: any) {
  const [openMarker, setOpenMarker] = useState<string | null>(null);
  // Distingue fitBounds() programático (dataset novo) de um zoom real do
  // usuário — "zoom_changed" do Google Maps dispara para os dois casos, ao
  // contrário de "dragstart" (só dispara em arraste manual). Setado logo
  // antes de qualquer chamada de câmera programática e consumido (uma vez)
  // pelo handler de onZoomChanged abaixo.
  const programmaticCameraRef = useRef(false);

  // Memoizados por dados/config (não por posição de reprodução) — assim o
  // player não recalcula/re-renderiza essas listas a cada frame, só o
  // marcador ativo muda.
  const positionsToShow = useMemo(
    () =>
      onlyStartEnd
        ? []
        : showPoints
          ? positions.length > 400
            ? positions.filter(
                (_: DetailedPosition, i: number) => i % Math.ceil(positions.length / 400) === 0,
              )
            : positions
          : [],
    [positions, showPoints, onlyStartEnd],
  );

  const path = useMemo(
    () => positions.map((p: DetailedPosition) => ({ lat: p.lat, lng: p.lng })),
    [positions],
  );

  return (
    <APIProvider apiKey={apiKey}>
      <GoogleMap
        mapId={resolveGoogleMapId()}
        styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
        defaultCenter={{ lat: -14.2, lng: -51.9 }}
        defaultZoom={4}
        disableDefaultUI
        zoomControl
        clickableIcons={false}
        gestureHandling="greedy"
        onDragstart={() => onManualCameraChange?.()}
        onZoomChanged={() => {
          if (programmaticCameraRef.current) {
            programmaticCameraRef.current = false;
            return;
          }
          onManualCameraChange?.();
        }}
        style={{ height: "100%", width: "100%" }}
      >
        <FitBounds
          positions={positions}
          mapRef={mapRef}
          programmaticCameraRef={programmaticCameraRef}
        />
        <CenterOnPlayback
          autoCenter={autoCenter}
          currentLatLng={currentLatLng}
          onManualCameraChange={onManualCameraChange}
        />

        {/* Trajeto: halo branco por baixo (borda) + linha azul GPS Drive por
            cima — a API do Google Maps não expõe line-cap/line-join, então
            o efeito de "borda arredondada" é simulado empilhando duas
            polylines; o antialiasing do próprio renderer vetorial do Maps
            já cuida da suavidade do traço. */}
        {path.length >= 2 && (
          <>
            <GooglePolyline
              path={path}
              strokeColor="#ffffff"
              strokeWeight={routeWeight + 5}
              strokeOpacity={Math.min(1, routeOpacity + 0.1)}
              zIndex={1}
            />
            <GooglePolyline
              path={path}
              strokeColor={routeColor}
              strokeWeight={routeWeight}
              strokeOpacity={routeOpacity}
              zIndex={2}
            />
          </>
        )}

        {positions.length > 0 && (
          <MarkerWithInfo
            id="start"
            position={{ lat: positions[0].lat, lng: positions[0].lng }}
            zIndex={500}
            openMarker={openMarker}
            setOpenMarker={setOpenMarker}
            info={
              <div className="text-xs text-slate-900">
                🟢 Início — {formatDateTime(positions[0].timestamp)}
              </div>
            }
          >
            <EmojiMarker emoji="🟢" ring="#16a34a" />
          </MarkerWithInfo>
        )}
        {positions.length > 1 && (
          <MarkerWithInfo
            id="end"
            position={{
              lat: positions[positions.length - 1].lat,
              lng: positions[positions.length - 1].lng,
            }}
            zIndex={500}
            openMarker={openMarker}
            setOpenMarker={setOpenMarker}
            info={
              <div className="text-xs text-slate-900">
                🔴 Fim — {formatDateTime(positions[positions.length - 1].timestamp)}
              </div>
            }
          >
            <EmojiMarker emoji="🔴" ring="#dc2626" />
          </MarkerWithInfo>
        )}

        {positionsToShow.map((p: DetailedPosition) => (
          <MarkerWithInfo
            key={p.id}
            id={`pt-${p.id}`}
            position={{ lat: p.lat, lng: p.lng }}
            openMarker={openMarker}
            setOpenMarker={setOpenMarker}
            info={
              <div className="space-y-0.5 text-xs text-slate-900">
                <div className="font-semibold">{formatDateTime(p.timestamp)}</div>
                <div>Velocidade: {p.speed} km/h</div>
                <div>Endereço: {p.address ?? "—"}</div>
              </div>
            }
          >
            <Dot color="#64748b" />
          </MarkerWithInfo>
        ))}

        {showStops &&
          stops.map((s: RouteStop, i: number) => (
            <MarkerWithInfo
              key={`stop-${i}`}
              id={`stop-${i}`}
              position={{ lat: s.lat, lng: s.lng }}
              zIndex={400}
              openMarker={openMarker}
              setOpenMarker={setOpenMarker}
              infoClassName="wp-stop-infowindow"
              info={
                <div className="min-w-[220px] rounded-xl bg-slate-900 px-4 py-3.5 text-white">
                  <div className="mb-3 text-center text-[10px] font-bold uppercase tracking-[0.15em] text-sky-400">
                    Parada
                  </div>
                  <div className="space-y-3">
                    <StopRow emoji="🟢" label="Início" value={formatDateTime(s.startTime)} />
                    <StopRow emoji="🔴" label="Fim" value={formatDateTime(s.endTime)} />
                    <StopRow emoji="⏱" label="Duração" value={formatDuration(s.durationMs)} />
                    <StopRow emoji="📍" label="Endereço" value={s.address ?? "—"} />
                    <StopRow
                      emoji="🚗"
                      label="Hodômetro"
                      value={`${s.endOdometerKm.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`}
                    />
                    <StopRow emoji="🅿" label="Tempo parado" value={formatDuration(s.durationMs)} />
                  </div>
                </div>
              }
            >
              <EmojiMarker emoji="🅿" ring="#f97316" size={26} />
            </MarkerWithInfo>
          ))}

        {showEvents &&
          events.map((e: VehicleEvent) => {
            const pid = (e.attributes as { positionId?: number } | undefined)?.positionId;
            const pos = typeof pid === "number" ? positionById.get(pid) : undefined;
            if (!pos) return null;
            return (
              <MarkerWithInfo
                key={`event-${e.id}`}
                id={`event-${e.id}`}
                position={{ lat: pos.lat, lng: pos.lng }}
                zIndex={400}
                openMarker={openMarker}
                setOpenMarker={setOpenMarker}
                info={
                  <div className="space-y-0.5 text-xs text-slate-900">
                    <div className="font-semibold">{describeEvent(e)}</div>
                    <div>{formatDateTime(e.eventTime)}</div>
                  </div>
                }
              >
                <EmojiMarker emoji="⚠" ring="#eab308" size={26} />
              </MarkerWithInfo>
            );
          })}

        {currentLatLng && (
          <AdvancedMarker
            position={{ lat: currentLatLng.lat, lng: currentLatLng.lng }}
            zIndex={1000}
          >
            <div className="wp-live-dot" style={{ borderRadius: "50%" }}>
              <Dot color={routeColor} size={16} />
            </div>
          </AdvancedMarker>
        )}
        {highlightPosition && (
          <AdvancedMarker
            position={{ lat: highlightPosition.lat, lng: highlightPosition.lng }}
            zIndex={900}
          >
            <Dot color="#facc15" size={14} />
          </AdvancedMarker>
        )}
      </GoogleMap>
    </APIProvider>
  );
}

// ============================================================================
// Relatório para PDF — layout próprio (A4), não é a tela impressa. Sem
// biblioteca de PDF: usa window.print() sobre este DOM dedicado, com o
// resto da tela escondida via CSS (@media print acima).
//
// Mapa (2026-10-07, "baixo o histórico e o mapa não aparece, só um risco"):
// a Static Maps API do Google segue 403 na chave, então o PDF caía sempre na
// projeção SVG sem ruas — só a linha vermelha. Agora são tiles do
// OpenStreetMap (<img> comum, imprime normal) com o traçado, paradas e
// cortes de energia por cima — mesmo esquema do PDF de Relatórios
// (computeTileViewport em reports-export.ts). Posições em % da caixa, pra
// escalar com a largura da página sem cortar.
// ============================================================================

const PRINT_MAP_W = 720;
const PRINT_MAP_H = 360;

// Espera os tiles do mapa do PDF carregarem antes de abrir a impressão —
// senão o mapa sai cinza. Os <img> ficam em display:none na tela, mas o
// navegador baixa mesmo assim (não são lazy). Teto de 8 s.
function printHistoryReport(onDone: () => void) {
  window.addEventListener("afterprint", onDone, { once: true });
  // Rede de segurança pra navegador que não dispara afterprint.
  setTimeout(onDone, 120_000);
  const pending = Array.from(
    document.querySelectorAll<HTMLImageElement>(".wp-print-only img"),
  ).filter((img) => !img.complete);
  let fired = false;
  const go = () => {
    if (fired) return;
    fired = true;
    window.print();
  };
  if (pending.length === 0) {
    go();
    return;
  }
  let left = pending.length;
  const one = () => {
    left -= 1;
    if (left <= 0) go();
  };
  for (const img of pending) {
    img.addEventListener("load", one, { once: true });
    img.addEventListener("error", one, { once: true });
  }
  setTimeout(go, 8000);
}

const ALARM_LABELS: Record<string, string> = {
  vibration: "Vibração",
  sos: "SOS / Pânico",
  sosAlarm: "SOS / Pânico",
  accident: "Acidente",
  removing: "Rastreador removido",
  hardCornering: "Curva brusca",
  hardAcceleration: "Aceleração brusca",
  hardBraking: "Frenagem brusca",
  jamming: "Bloqueio de sinal (jamming)",
  overspeed: "Excesso de velocidade",
  shock: "Impacto",
  door: "Porta aberta",
};

// Hora do corte por extenso. Rastreador sem alarme só percebe/avisa no
// pacote de status seguinte, então o corte real foi ENTRE a última leitura
// com energia e a primeira sem — mostra a janela em vez de cravar um horário.
function powerCutWhen(c: PowerCut): string {
  if (c.startedBeforePeriod)
    return `Antes de ${formatDateTime(c.startTime)} (já estava sem energia no início do período)`;
  if (c.lastPoweredAt)
    return `Entre ${formatDateTime(c.lastPoweredAt)} e ${formatTime(c.startTime)}`;
  return formatDateTime(c.startTime);
}

function describeEvent(e: VehicleEvent): string {
  if (e.type !== "alarm") return eventLabel(e.type);
  const power = powerAlarmLabel(e.attributes);
  if (power) return power;
  const raw = (e.attributes as { alarm?: string } | undefined)?.alarm;
  if (!raw) return "Alarme";
  return `Alarme: ${raw
    .split(",")
    .map((c) => ALARM_LABELS[c.trim()] ?? c.trim())
    .join(", ")}`;
}

function PrintRouteMap({
  positions,
  stops,
  powerCuts,
  routeColor,
}: {
  positions: DetailedPosition[];
  stops: RouteStop[];
  powerCuts: PowerCut[];
  routeColor: string;
}) {
  const view = useMemo(() => {
    const route = downsamplePoints(positions, 1500);
    const cutPoints = powerCuts
      .filter((c) => c.lat != null && c.lng != null && isValidCoordinate(c.lat, c.lng))
      .map((c, i) => ({ n: i + 1, lat: c.lat!, lng: c.lng! }));
    const vp = computeTileViewport(
      [...route, ...stops.map((s) => ({ lat: s.lat, lng: s.lng })), ...cutPoints],
      PRINT_MAP_W,
      PRINT_MAP_H,
      36,
    );
    if (!vp || route.length < 1) return null;
    const pts = route
      .map((p) => vp.project(p.lat, p.lng).map((v) => v.toFixed(1)).join(","))
      .join(" ");
    return {
      vp,
      pts,
      start: vp.project(route[0].lat, route[0].lng),
      end: vp.project(route[route.length - 1].lat, route[route.length - 1].lng),
      stops: stops.slice(0, 300).map((s) => vp.project(s.lat, s.lng)),
      cuts: cutPoints.map((c) => ({ n: c.n, xy: vp.project(c.lat, c.lng) })),
    };
  }, [positions, stops, powerCuts]);

  if (!view) {
    return (
      <div
        className="rounded-lg py-6 text-center text-xs text-gray-500"
        style={{ border: "1px solid #dbe3ee", background: "#f8fafc" }}
      >
        Sem posições suficientes para desenhar o trajeto.
      </div>
    );
  }
  const pct = (v: number, total: number) => `${((v / total) * 100).toFixed(3)}%`;
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: `${PRINT_MAP_W} / ${PRINT_MAP_H}`,
        overflow: "hidden",
        borderRadius: 10,
        border: "1px solid #dbe3ee",
        background: "#e5e7eb",
      }}
    >
      {view.vp.tiles.map((t) => (
        <img
          key={t.url}
          src={t.url}
          alt=""
          style={{
            position: "absolute",
            left: pct(t.x, PRINT_MAP_W),
            top: pct(t.y, PRINT_MAP_H),
            width: pct(OSM_TILE_SIZE, PRINT_MAP_W),
            height: pct(OSM_TILE_SIZE, PRINT_MAP_H),
            maxWidth: "none",
          }}
        />
      ))}
      <svg
        viewBox={`0 0 ${PRINT_MAP_W} ${PRINT_MAP_H}`}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <polyline
          points={view.pts}
          fill="none"
          stroke="#ffffff"
          strokeWidth={8}
          strokeOpacity={0.9}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points={view.pts}
          fill="none"
          stroke={routeColor}
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {view.stops.map(([x, y], i) => (
          <circle key={`s${i}`} cx={x} cy={y} r={5} fill="#f97316" stroke="#fff" strokeWidth={1.5} />
        ))}
        <circle cx={view.start[0]} cy={view.start[1]} r={8} fill="#16a34a" stroke="#fff" strokeWidth={2.5} />
        <circle cx={view.end[0]} cy={view.end[1]} r={8} fill="#111827" stroke="#fff" strokeWidth={2.5} />
        {view.cuts.map(({ n, xy: [x, y] }) => (
          <g key={`c${n}`}>
            <circle cx={x} cy={y} r={11} fill="#dc2626" stroke="#fff" strokeWidth={2.5} />
            <text
              x={x}
              y={y + 4}
              textAnchor="middle"
              fontSize={11}
              fontWeight={700}
              fill="#fff"
              fontFamily="Arial, sans-serif"
            >
              {n}
            </text>
          </g>
        ))}
      </svg>
      <div
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          background: "rgba(255,255,255,.85)",
          fontSize: 8,
          padding: "1px 4px",
          color: "#333",
        }}
      >
        © OpenStreetMap
      </div>
    </div>
  );
}

// Mapa Google de verdade no PDF — o JS API normal (a Static Maps API dá
// 403 nessa chave). Sem mapId = renderização raster (tiles <img>), que sai
// na impressão; o mapa vetorial (WebGL) sai em branco. Largura fixa igual à
// área útil do A4 (182 mm ≈ 688 px) pra não redimensionar na hora de
// imprimir. Marcadores clássicos (google.maps.Marker) porque o
// AdvancedMarker exige mapId.
const PRINT_GMAP_W = 688;
const PRINT_GMAP_H = 400;

function PrintGoogleMapOverlays({
  route,
  stops,
  powerCuts,
  onReady,
}: {
  route: { lat: number; lng: number }[];
  stops: RouteStop[];
  powerCuts: PowerCut[];
  onReady: () => void;
}) {
  const map = useGoogleMap();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    if (!map || route.length === 0) return;
    const g = google.maps;
    const bounds = new g.LatLngBounds();
    for (const p of route) bounds.extend(p);
    for (const s of stops) bounds.extend({ lat: s.lat, lng: s.lng });
    const dot = (fill: string, scale: number) => ({
      path: g.SymbolPath.CIRCLE,
      scale,
      fillColor: fill,
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 2.5,
    });
    const markers: google.maps.Marker[] = [];
    for (const s of stops.slice(0, 300)) {
      markers.push(new g.Marker({ map, position: { lat: s.lat, lng: s.lng }, icon: dot("#f97316", 6), zIndex: 5 }));
    }
    markers.push(new g.Marker({ map, position: route[0], icon: dot("#16a34a", 9), zIndex: 10 }));
    markers.push(new g.Marker({ map, position: route[route.length - 1], icon: dot("#111827", 9), zIndex: 10 }));
    powerCuts.forEach((c, i) => {
      if (c.lat == null || c.lng == null || !isValidCoordinate(c.lat, c.lng)) return;
      bounds.extend({ lat: c.lat, lng: c.lng });
      markers.push(
        new g.Marker({
          map,
          position: { lat: c.lat, lng: c.lng },
          icon: dot("#dc2626", 12),
          label: { text: String(i + 1), color: "#ffffff", fontWeight: "700", fontSize: "12px" },
          zIndex: 20,
        }),
      );
    });
    let done = false;
    const ready = () => {
      if (done) return;
      done = true;
      // Folga pros últimos tiles/marcadores pintarem.
      setTimeout(() => onReadyRef.current(), 600);
    };
    const listener = g.event.addListenerOnce(map, "idle", () => {
      g.event.addListenerOnce(map, "tilesloaded", ready);
      // "tilesloaded" não dispara de novo se nada mudou após o fitBounds.
      setTimeout(ready, 4000);
    });
    map.fitBounds(bounds, 40);
    return () => {
      g.event.removeListener(listener);
      for (const m of markers) m.setMap(null);
    };
  }, [map, route, stops, powerCuts]);
  return null;
}

function PrintGoogleMap({
  apiKey,
  positions,
  stops,
  powerCuts,
  routeColor,
  onReady,
}: {
  apiKey: string;
  positions: DetailedPosition[];
  stops: RouteStop[];
  powerCuts: PowerCut[];
  routeColor: string;
  onReady: () => void;
}) {
  const route = useMemo(
    () => downsamplePoints(positions, 2000).map((p) => ({ lat: p.lat, lng: p.lng })),
    [positions],
  );
  if (route.length === 0) return null;
  return (
    <div
      style={{
        width: PRINT_GMAP_W,
        height: PRINT_GMAP_H,
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid #dbe3ee",
      }}
    >
      <APIProvider apiKey={apiKey}>
        <GoogleMap
          defaultCenter={route[0]}
          defaultZoom={13}
          disableDefaultUI
          clickableIcons={false}
          gestureHandling="none"
          keyboardShortcuts={false}
          style={{ width: "100%", height: "100%" }}
        >
          <PrintGoogleMapOverlays
            route={route}
            stops={stops}
            powerCuts={powerCuts}
            onReady={onReady}
          />
          {route.length >= 2 && (
            <>
              <GooglePolyline path={route} strokeColor="#ffffff" strokeWeight={9} strokeOpacity={0.9} zIndex={1} />
              <GooglePolyline path={route} strokeColor={routeColor} strokeWeight={5} strokeOpacity={1} zIndex={2} />
            </>
          )}
        </GoogleMap>
      </APIProvider>
    </div>
  );
}

// Faixa horizontal do período: verde = rodando (viagens do Traccar),
// laranja = parado, vermelho = sem energia. Dá pra bater o olho e ver em
// que hora do dia cada coisa aconteceu.
function PrintTimeline({
  range,
  trips,
  stops,
  powerCuts,
  lastFixTime,
}: {
  range: { from: string; to: string };
  trips: Trip[];
  stops: RouteStop[];
  powerCuts: PowerCut[];
  lastFixTime?: string;
}) {
  const start = new Date(range.from).getTime();
  // Período "hoje" vai até 23:59 — corta no agora pra não sobrar uma faixa
  // vazia de futuro.
  const end = Math.max(
    start + 60_000,
    Math.min(new Date(range.to).getTime(), Math.max(Date.now(), lastFixTime ? new Date(lastFixTime).getTime() : 0)),
  );
  const span = end - start;
  const seg = (a: string, b: string | undefined, color: string, key: string, h = 14, top = 0) => {
    const s = Math.max(start, new Date(a).getTime());
    const e = Math.min(end, b ? new Date(b).getTime() : end);
    if (e <= s && b) return null;
    const left = ((s - start) / span) * 100;
    const width = Math.max(0.5, ((Math.max(e, s) - s) / span) * 100);
    return (
      <div
        key={key}
        style={{
          position: "absolute",
          left: `${left}%`,
          width: `${Math.min(width, 100 - left)}%`,
          top,
          height: h,
          background: color,
          borderRadius: 2,
        }}
      />
    );
  };
  const ticks = Array.from({ length: 7 }, (_, i) => start + (span * i) / 6);
  const sameDay = format(start, "yyyy-MM-dd") === format(end, "yyyy-MM-dd");
  return (
    <div>
      <div
        style={{
          position: "relative",
          height: 22,
          background: "#f1f5f9",
          borderRadius: 4,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        {stops.map((s, i) => seg(s.startTime, s.endTime, "#fdba74", `st${i}`, 22))}
        {trips.map((t, i) => seg(t.startTime, t.endTime, "#22c55e", `tr${i}`, 22))}
        {powerCuts.map((c, i) => seg(c.startTime, c.endTime, "#dc2626", `pc${i}`, 22))}
      </div>
      <div style={{ position: "relative", height: 12 }} className="text-[8px] text-gray-500">
        {ticks.map((t, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${(i / 6) * 100}%`,
              transform: i === 0 ? "none" : i === 6 ? "translateX(-100%)" : "translateX(-50%)",
            }}
          >
            {format(t, sameDay ? "HH:mm" : "dd/MM HH:mm")}
          </span>
        ))}
      </div>
    </div>
  );
}

// Gráfico da tensão da bateria do veículo no PDF — é a prova mais clara de
// um corte (ex.: 12,2 V → 0 V às 17:20). Só aparece se o rastreador manda
// a tensão.
function PrintVoltageChart({
  readings,
  powerCuts,
}: {
  readings: VoltageReading[];
  powerCuts: PowerCut[];
}) {
  const W = 688;
  const H = 130;
  const padL = 30;
  const padB = 16;
  const data = downsamplePoints(readings, 700);
  const t0 = data[0].t;
  const t1 = Math.max(data[data.length - 1].t, t0 + 60_000);
  const vMax = Math.max(15, Math.ceil(Math.max(...data.map((r) => r.volts))));
  const x = (t: number) => padL + ((t - t0) / (t1 - t0)) * (W - padL - 4);
  const y = (v: number) => 4 + (1 - v / vMax) * (H - padB - 8);
  const pts = data.map((r) => `${x(r.t).toFixed(1)},${y(r.volts).toFixed(1)}`).join(" ");
  const sameDay = format(t0, "yyyy-MM-dd") === format(t1, "yyyy-MM-dd");
  const ticks = Array.from({ length: 6 }, (_, i) => t0 + ((t1 - t0) * i) / 5);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff" }}>
      {[0, 6, 12].filter((v) => v <= vMax).map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - 4} y1={y(v)} y2={y(v)} stroke="#e2e8f0" />
          <text x={padL - 4} y={y(v) + 3} textAnchor="end" fontSize={8} fill="#64748b">
            {v} V
          </text>
        </g>
      ))}
      {powerCuts
        .filter((c) => !c.startedBeforePeriod)
        .map((c, i) => {
          const cx = x(new Date(c.startTime).getTime());
          if (cx < padL || cx > W) return null;
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={4} y2={H - padB} stroke="#dc2626" strokeDasharray="3 2" />
              <text x={cx + 3} y={12} fontSize={8} fill="#dc2626" fontWeight={700}>
                ⚡ corte {format(new Date(c.startTime), "HH:mm")}
              </text>
            </g>
          );
        })}
      <polyline points={pts} fill="none" stroke="#2563eb" strokeWidth={1.6} strokeLinejoin="round" />
      {ticks.map((t, i) => (
        <text
          key={i}
          x={x(t)}
          y={H - 4}
          fontSize={8}
          fill="#64748b"
          textAnchor={i === 0 ? "start" : i === 5 ? "end" : "middle"}
        >
          {format(t, sameDay ? "HH:mm" : "dd/MM HH:mm")}
        </text>
      ))}
    </svg>
  );
}

interface ChronoItem {
  t: number;
  icon: string;
  text: string;
  tone?: "danger" | "ok" | "muted" | "info";
}

// Cronologia detalhada do PDF (pedido 2026-10-07 "quero que mostre
// realmente o que aconteceu"): junta, em ordem de horário, corte/retorno
// de energia, ignição, saídas/chegadas, alarmes, períodos sem posição de
// GPS e a bateria interna do rastreador.
function buildChronology(opts: {
  positions: DetailedPosition[];
  events: VehicleEvent[];
  trips: Trip[];
  powerCuts: PowerCut[];
}): ChronoItem[] {
  const items: ChronoItem[] = [];
  const ts = (iso: string) => new Date(iso).getTime();
  for (const c of opts.powerCuts) {
    const volt = c.voltageBefore != null ? ` (bateria do veículo: ${c.voltageBefore} V → 0 V)` : "";
    items.push({
      t: ts(c.startTime),
      icon: "⚡",
      tone: "danger",
      text: c.startedBeforePeriod
        ? `Veículo já estava SEM ALIMENTAÇÃO no início do período`
        : `ALIMENTAÇÃO CORTADA — ${c.label}${volt}${
            c.lastPoweredAt ? `. Última leitura com energia: ${formatDateTime(c.lastPoweredAt)}` : ""
          }`,
    });
    if (c.endTime)
      items.push({ t: ts(c.endTime), icon: "🔌", tone: "ok", text: "Alimentação voltou" });
  }
  for (const e of opts.events) {
    if (e.type === "ignitionOn")
      items.push({ t: ts(e.eventTime), icon: "🔑", tone: "info", text: "Ignição LIGADA" });
    else if (e.type === "ignitionOff")
      items.push({ t: ts(e.eventTime), icon: "🔑", text: "Ignição desligada" });
    else if (e.type === "alarm") {
      const power = powerAlarmLabel(e.attributes);
      // Corte/retorno já entram pelo detector acima (sem duplicar).
      if (power) continue;
      items.push({ t: ts(e.eventTime), icon: "🚨", tone: "danger", text: describeEvent(e) });
    } else if (e.type !== "deviceMoving" && e.type !== "deviceStopped")
      items.push({ t: ts(e.eventTime), icon: "•", tone: "muted", text: describeEvent(e) });
  }
  for (const tr of opts.trips) {
    items.push({
      t: ts(tr.startTime),
      icon: "🚗",
      tone: "info",
      text: `Começou a andar${tr.startAddress ? ` — saiu de ${tr.startAddress}` : ""}`,
    });
    items.push({
      t: ts(tr.endTime),
      icon: "🅿",
      text: `Parou${tr.endAddress ? ` em ${tr.endAddress}` : ""} — ${tr.distanceKm.toFixed(1)} km em ${formatDuration(
        tr.durationMs,
      )}, vel. máx. ${Math.round(tr.maxSpeed)} km/h`,
    });
  }
  // Períodos sem posição de GPS (≥ 30 min) que importam: o veículo
  // reapareceu em outro lugar (deslocou sem registro) ou estava sem energia.
  // Parado com GPS dormindo é normal e não entra.
  const fixes = opts.positions.filter(
    (p) => typeof p.attributes?.sat === "number" && (p.attributes.sat as number) > 0,
  );
  for (let i = 1; i < fixes.length; i++) {
    const a = fixes[i - 1];
    const b = fixes[i];
    const gap = ts(b.timestamp) - ts(a.timestamp);
    if (gap < 30 * 60_000) continue;
    const moved = haversineKm({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng });
    const unpowered = opts.powerCuts.some(
      (c) => ts(c.startTime) < ts(b.timestamp) && (!c.endTime || ts(c.endTime) > ts(a.timestamp)),
    );
    if (moved < 0.2 && !unpowered) continue;
    items.push({
      t: ts(a.timestamp) + 1,
      icon: "📡",
      tone: moved >= 0.2 ? "danger" : "muted",
      text:
        `Sem posição de GPS por ${formatDuration(gap)} (até ${formatDateTime(b.timestamp)})` +
        (moved >= 0.2
          ? ` — o veículo reapareceu a ${formatSegmentDistance(moved)} daqui, deslocamento sem registro`
          : " — rastreador sem energia, em economia (só sinal de vida)"),
    });
  }
  let lastLevel: number | undefined;
  for (const p of opts.positions) {
    const lvl = p.attributes?.batteryLevel;
    if (typeof lvl !== "number" || lvl === lastLevel) continue;
    if (lastLevel != null)
      items.push({
        t: p.serverTime ? ts(p.serverTime) : ts(p.timestamp),
        icon: "🔋",
        tone: "muted",
        text: `Bateria interna do rastreador: ${lastLevel}% → ${lvl}%`,
      });
    lastLevel = lvl;
  }
  return items.sort((a, b) => a.t - b.t);
}

function PrintReport({
  vehicle,
  range,
  summary,
  movingMs,
  stoppedMs,
  positions,
  stops,
  trips,
  events,
  powerCuts,
  routeColor,
  googleMapsApiKey,
  preparing,
  onPrintMapReady,
}: {
  vehicle: Vehicle;
  range: { from: string; to: string };
  summary?: { distanceKm: number; averageSpeed: number; maxSpeed: number };
  movingMs: number;
  stoppedMs: number;
  positions: DetailedPosition[];
  stops: RouteStop[];
  trips: Trip[];
  events: VehicleEvent[];
  powerCuts: PowerCut[];
  routeColor: string;
  // Só quando a chave do Google funciona; senão o PDF usa o mapa OSM.
  googleMapsApiKey: string | null;
  // Clique em PDF em andamento — monta o mapa Google fora da tela.
  preparing: boolean;
  onPrintMapReady: () => void;
}) {
  const validStopsForMap = useMemo(
    () => stops.filter((s) => isValidCoordinate(s.lat, s.lng)),
    [stops],
  );
  // Última tensão de alimentação registrada no período (varre de trás pra
  // frente sem copiar o array). O Traccar só envia "power" quando o
  // dispositivo suporta, então nem toda posição tem.
  const voltageReadings = useMemo(() => vehicleVoltageReadings(positions), [positions]);
  const lastPower = voltageReadings[voltageReadings.length - 1]?.volts;
  const chronology = useMemo(
    () => buildChronology({ positions, events, trips, powerCuts }),
    [positions, events, trips, powerCuts],
  );
  const ignitionOnCount = events.filter((e) => e.type === "ignitionOn").length;
  const now = new Date();
  const totalCutMs = powerCuts.reduce((s, c) => s + (c.durationMs ?? 0), 0);
  const cutPlace = (c: PowerCut) =>
    c.address ||
    (c.lat != null && c.lng != null ? `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}` : "—");

  return (
    <div className={cn("wp-print-only bg-white p-0 text-black", preparing && "wp-print-preparing")}>
      <div
        className="flex items-start justify-between rounded-t-lg pb-3"
        style={{ borderBottom: `3px solid ${routeColor}` }}
      >
        <div className="flex items-center gap-3">
          <img src={logoAsset.url} alt="GPS Drive" className="h-12 w-auto" />
          <div>
            <div className="text-lg font-bold tracking-tight">GPS Drive</div>
            <div className="text-xs font-medium text-gray-500">Relatório Detalhado de Histórico</div>
          </div>
        </div>
        <div className="text-right text-xs text-gray-600">
          <div>Emitido em {format(now, "dd/MM/yyyy")}</div>
          <div>às {format(now, "HH:mm:ss")}</div>
        </div>
      </div>

      <div
        className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs"
        style={{ border: "1px solid #e5e7eb" }}
      >
        <div>
          <div className="text-[9px] font-semibold uppercase text-gray-500">Veículo</div>
          <div className="font-bold">{vehicle.name}</div>
        </div>
        <div>
          <div className="text-[9px] font-semibold uppercase text-gray-500">Placa</div>
          <div className="font-bold">{vehicle.plate || "—"}</div>
        </div>
        <div>
          <div className="text-[9px] font-semibold uppercase text-gray-500">Período</div>
          <div className="font-bold">
            {formatDateTime(range.from)} — {formatDateTime(range.to)}
          </div>
        </div>
      </div>

      {/* Energia em destaque — pedido 2026-10-07: mostrar a hora do corte
          e, se não houve, dizer isso com todas as letras. */}
      {powerCuts.length === 0 ? (
        <div
          className="mt-3 flex items-center gap-3 rounded-lg px-3 py-2.5"
          style={{ background: "#f0fdf4", border: "1.5px solid #86efac" }}
        >
          <span className="text-2xl leading-none">✅</span>
          <div>
            <div className="text-sm font-bold" style={{ color: "#15803d" }}>
              Não houve corte de energia no período
            </div>
            <div className="text-[10px] text-gray-600">
              Nenhum alarme de corte de energia/bateria violada e nenhuma leitura de
              alimentação externa desligada foi registrada pelo rastreador
              {lastPower != null ? ` · última tensão da alimentação: ${lastPower} V` : ""}.
            </div>
          </div>
        </div>
      ) : (
        <div
          className="mt-3 rounded-lg px-3 py-2.5"
          style={{ background: "#fef2f2", border: "1.5px solid #fca5a5", pageBreakInside: "avoid" }}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl leading-none">⚡</span>
            <div>
              <div className="text-sm font-bold" style={{ color: "#b91c1c" }}>
                {powerCuts.length === 1
                  ? "1 corte de energia no período"
                  : `${powerCuts.length} cortes de energia no período`}
              </div>
              <div className="text-[10px] text-gray-700">
                Primeiro corte: <b>{powerCutWhen(powerCuts[0])}</b>
                {totalCutMs > 0 && ` · tempo total sem energia: ${formatDuration(totalCutMs)}`}
                {powerCuts.some((c) => !c.endTime) &&
                  " · a energia não voltou até o fim do período"}
              </div>
            </div>
          </div>
          <table className="mt-2 w-full border-collapse text-[10px]">
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid #fca5a5", color: "#7f1d1d" }}>
                <th className="py-1">#</th>
                <th className="py-1">Hora do corte</th>
                <th className="py-1">Energia voltou</th>
                <th className="py-1">Tempo sem energia</th>
                <th className="py-1">Tipo</th>
                <th className="py-1">Local</th>
              </tr>
            </thead>
            <tbody>
              {powerCuts.map((c, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #fee2e2" }}>
                  <td className="py-1">
                    <span
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white"
                      style={{ background: "#dc2626" }}
                    >
                      {i + 1}
                    </span>
                  </td>
                  <td className="py-1 font-bold" style={{ color: "#b91c1c" }}>
                    {powerCutWhen(c)}
                  </td>
                  <td className="py-1">{c.endTime ? formatDateTime(c.endTime) : "Não voltou no período"}</td>
                  <td className="py-1">{c.durationMs != null ? formatDuration(c.durationMs) : "—"}</td>
                  <td className="py-1">{c.label}</td>
                  <td className="py-1">{cutPlace(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-1.5 text-sm font-bold">Resumo</div>
        <div className="grid grid-cols-5 gap-2 text-xs">
          <SummaryBox
            label="Distância"
            value={`${(summary?.distanceKm ?? 0).toFixed(1)} km`}
            accent={routeColor}
          />
          <SummaryBox label="Em movimento" value={formatDuration(movingMs)} accent="#16a34a" />
          <SummaryBox label="Parado" value={formatDuration(stoppedMs)} accent="#f97316" />
          <SummaryBox label="Vel. máxima" value={`${summary?.maxSpeed ?? 0} km/h`} accent="#dc2626" />
          <SummaryBox label="Vel. média" value={`${summary?.averageSpeed ?? 0} km/h`} accent={routeColor} />
          <SummaryBox label="Viagens" value={String(trips.length)} accent="#16a34a" />
          <SummaryBox label="Paradas" value={String(validStopsForMap.length)} accent="#f97316" />
          <SummaryBox label="Ignição ligada" value={`${ignitionOnCount}x`} accent="#2563eb" />
          <SummaryBox
            label="Cortes de energia"
            value={String(powerCuts.length)}
            accent={powerCuts.length > 0 ? "#dc2626" : "#16a34a"}
          />
          <SummaryBox
            label="Alimentação"
            value={lastPower != null ? `${lastPower} V` : "—"}
            accent="#2563eb"
          />
        </div>
      </div>

      {range && (trips.length > 0 || stops.length > 0 || powerCuts.length > 0) && (
        <div className="mt-4" style={{ pageBreakInside: "avoid" }}>
          <div className="mb-1.5 text-sm font-bold">Linha do tempo</div>
          <PrintTimeline
            range={range}
            trips={trips}
            stops={stops}
            powerCuts={powerCuts}
            lastFixTime={positions[positions.length - 1]?.timestamp}
          />
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-600">
            <LegendDot color="#22c55e" label="Em movimento" />
            <LegendDot color="#fdba74" label="Parado" />
            <LegendDot color="#dc2626" label="Sem energia" />
          </div>
        </div>
      )}

      <div className="mt-4" style={{ pageBreakInside: "avoid" }}>
        <div className="mb-1.5 text-sm font-bold">Mapa do trajeto</div>
        {googleMapsApiKey && preparing ? (
          <PrintGoogleMap
            apiKey={googleMapsApiKey}
            positions={positions}
            stops={validStopsForMap}
            powerCuts={powerCuts}
            routeColor={routeColor}
            onReady={onPrintMapReady}
          />
        ) : (
          <PrintRouteMap
            positions={positions}
            stops={validStopsForMap}
            powerCuts={powerCuts}
            routeColor={routeColor}
          />
        )}
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-600">
          <LegendDot color="#16a34a" label="Início" />
          <LegendDot color="#111827" label="Fim" />
          {validStopsForMap.length > 0 && <LegendDot color="#f97316" label="Parada" />}
          {powerCuts.length > 0 && <LegendDot color="#dc2626" label="Corte de energia (nº)" />}
          <span style={{ color: routeColor }}>━ Trajeto percorrido</span>
        </div>
      </div>

      {trips.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-sm font-bold">Viagens ({trips.length})</div>
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="border-b border-black text-left">
                <th className="py-1">Saída</th>
                <th className="py-1">Chegada</th>
                <th className="py-1">Origem → Destino</th>
                <th className="py-1 text-right">Distância</th>
                <th className="py-1 text-right">Duração</th>
                <th className="py-1 text-right">Vel. máx.</th>
              </tr>
            </thead>
            <tbody>
              {trips.slice(0, 80).map((t, i) => (
                <tr key={t.id || i} className="border-b border-gray-300" style={{ pageBreakInside: "avoid" }}>
                  <td className="py-0.5 whitespace-nowrap">{formatDateTime(t.startTime)}</td>
                  <td className="py-0.5 whitespace-nowrap">{formatTime(t.endTime)}</td>
                  <td className="py-0.5">
                    {t.startAddress || "—"} <b>→</b> {t.endAddress || "—"}
                  </td>
                  <td className="py-0.5 text-right">{t.distanceKm.toFixed(1)} km</td>
                  <td className="py-0.5 text-right">{formatDuration(t.durationMs)}</td>
                  <td className="py-0.5 text-right">{Math.round(t.maxSpeed)} km/h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-1 text-sm font-bold">Paradas ({stops.length})</div>
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="border-b border-black text-left">
              <th className="py-1">Chegou</th>
              <th className="py-1">Saiu</th>
              <th className="py-1">Duração</th>
              <th className="py-1">Endereço</th>
            </tr>
          </thead>
          <tbody>
            {stops.slice(0, 80).map((s, i) => (
              <tr key={i} className="border-b border-gray-300" style={{ pageBreakInside: "avoid" }}>
                <td className="py-0.5 whitespace-nowrap">{formatDateTime(s.startTime)}</td>
                <td className="py-0.5 whitespace-nowrap">{formatTime(s.endTime)}</td>
                <td className="py-0.5">{formatDuration(s.durationMs)}</td>
                <td className="py-0.5">{s.address ?? "—"}</td>
              </tr>
            ))}
            {stops.length === 0 && (
              <tr>
                <td colSpan={4} className="py-2 text-center text-gray-500">
                  Nenhuma parada no período.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {voltageReadings.length >= 2 && (
        <div className="mt-4" style={{ pageBreakInside: "avoid" }}>
          <div className="mb-1 text-sm font-bold">Tensão da bateria do veículo</div>
          <PrintVoltageChart readings={voltageReadings} powerCuts={powerCuts} />
          <div className="mt-0.5 text-[9px] text-gray-500">
            Leitura enviada pelo rastreador. 0 V = rastreador sem alimentação do veículo (bateria
            ou cabo desconectado), funcionando só com a bateria interna.
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-1 text-sm font-bold">Cronologia detalhada ({chronology.length})</div>
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="border-b border-black text-left">
              <th className="py-1 pl-1">Hora</th>
              <th className="py-1">O que aconteceu</th>
            </tr>
          </thead>
          <tbody>
            {chronology.slice(0, 400).map((item, i) => (
              <tr
                key={i}
                className="border-b border-gray-300"
                style={{
                  pageBreakInside: "avoid",
                  ...(item.tone === "danger"
                    ? { background: "#fee2e2", color: "#991b1b", fontWeight: 700 }
                    : item.tone === "ok"
                      ? { background: "#dcfce7", color: "#166534", fontWeight: 600 }
                      : item.tone === "muted"
                        ? { color: "#64748b" }
                        : item.tone === "info"
                          ? { color: "#1e3a8a" }
                          : {}),
                }}
              >
                <td className="py-0.5 pl-1 align-top whitespace-nowrap">
                  {format(item.t, "dd/MM HH:mm:ss")}
                </td>
                <td className="py-0.5">
                  {item.icon} {item.text}
                </td>
              </tr>
            ))}
            {chronology.length === 0 && (
              <tr>
                <td colSpan={2} className="py-2 text-center text-gray-500">
                  Nenhum acontecimento no período.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {chronology.length > 400 && (
          <div className="mt-1 text-[9px] text-gray-500">
            Mostrando os 400 primeiros de {chronology.length} registros — a lista completa sai no Excel.
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between border-t border-gray-400 pt-1 text-[9px] text-gray-500">
        <span>gpsdrive.com.br</span>
        <span>Gerado em {format(now, "dd/MM/yyyy HH:mm")}</span>
      </div>
    </div>
  );
}

function SummaryBox({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div
      className="rounded-lg bg-gray-50 px-2.5 py-1.5"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm font-bold text-gray-900">{value}</div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ background: color, border: "1.5px solid white", boxShadow: "0 0 0 1px #cbd5e1" }}
      />
      {label}
    </span>
  );
}
